import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  LinearProgress,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// ── Type definitions ─────────────────────────────────────────────────
interface CardProps {
  title?: string;
  icon?: string;
  rightText?: string;
  children: React.ReactNode;
  noPad?: boolean;
  sx?: object;
}

interface DateRange {
  start: Date | null;
  end: Date | null;
}

type PresetId =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "last3m"
  | "custom";

// ── API Base URL ────────────────────────────────────────────────────
const API_BASE = "https://trafficmonsterservice.ocesha.com";

// ── API fetch helper ────────────────────────────────────────────────
async function fetchDashboardData(range?: DateRange | null): Promise<any> {
  let url = `${API_BASE}/dashboard`;
  const params = new URLSearchParams();
  if (range?.start) params.append("start_date", range.start.toISOString().split("T")[0]);
  if (range?.end)   params.append("end_date",   range.end.toISOString().split("T")[0]);
  const qs = params.toString();
  if (qs) url += `?${qs}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API responded with status ${res.status}`);
  return res.json();
}

// ── Campaigns API ────────────────────────────────────────────────
async function fetchCampaigns(): Promise<any> {
  const res = await fetch(`${API_BASE}/campaigns`);
  if (!res.ok) throw new Error(`Campaigns API responded with status ${res.status}`);
  return res.json();
}

async function pauseCampaign(campaignId: string): Promise<any> {
  const res = await fetch(`${API_BASE}/campaigns/${campaignId}/pause`, { method: "POST" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Pause failed: ${res.status}`);
  }
  return res.json();
}

async function resumeCampaign(campaignId: string): Promise<any> {
  const res = await fetch(`${API_BASE}/campaigns/${campaignId}/resume`, { method: "POST" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Resume failed: ${res.status}`);
  }
  return res.json();
}

// ── Lead Stats API ──────────────────────────────────────────────────
// Returns: { total: N, by_tier: {"Tier 1": N, "Tier 2": N, ...}, by_icp: {"Consultants": N, ...}, by_status: {...} }
async function fetchLeadStats(): Promise<any> {
  const res = await fetch(`${API_BASE}/leads/stats`);
  if (!res.ok) throw new Error(`Lead stats API responded with status ${res.status}`);
  return res.json();
}

// ── Scraped Stats API ───────────────────────────────────────────────
async function fetchScrapedStats(): Promise<any> {
  const res = await fetch(`${API_BASE}/stats/scraped`);
  if (!res.ok) throw new Error(`Scraped stats API responded with status ${res.status}`);
  return res.json();
}

// ── Leads API ───────────────────────────────────────────────────────
// Returns: { total: N, leads: [{full_name, company, primary_icp, priority_tier, fit_score, email, linkedin_url, instantly_pushed_at, industry, ...}] }
// NOTE: backend now uses page/per_page pagination. This helper sends per_page
// (kept the `limit`/`skip` names off to match the live endpoint).
async function fetchLeads(params?: {
  icp?: string;
  tier?: string;
  status?: string;
  q?: string;
  page?: number;
  per_page?: number;
}): Promise<any> {
  const urlParams = new URLSearchParams();
  if (params?.icp)      urlParams.append("icp",      params.icp);
  if (params?.tier)     urlParams.append("tier",     params.tier);
  if (params?.status)   urlParams.append("status",   params.status);
  if (params?.q && params.q.trim()) urlParams.append("q", params.q.trim());
  if (params?.page)     urlParams.append("page",     String(params.page));
  if (params?.per_page) urlParams.append("per_page", String(params.per_page));
  const qs  = urlParams.toString();
  const url = `${API_BASE}/leads${qs ? `?${qs}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Leads API responded with status ${res.status}`);
  return res.json();
}

// ── Scheduler Status API ────────────────────────────────────────────
// Returns: { auto_push_scheduler: { running, next_check, daily_limit, sent_today, remaining_today, pending_jobs, current_utc },
//            scraper_scheduler:   { running, total_cycles, total_leads, next_run, ... } }
async function fetchSchedulerStatus(): Promise<any> {
  const res = await fetch(`${API_BASE}/scheduler/status`);
  if (!res.ok) throw new Error(`Scheduler status API responded with status ${res.status}`);
  return res.json();
}

// ── Push Lead to Instantly ─────────────────────────────────────────
// POST /push-lead  — pushes a single lead dict to Instantly
async function pushLeadToInstantly(lead: Record<string, any>): Promise<any> {
  const res = await fetch(`${API_BASE}/push-lead`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lead),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Push failed (${res.status})`);
  return data;
}

// ── Assign CRM leads to campaign ────────────────────────────────────
// POST /assign-crm-leads-to-campaign?campaign_id=...
async function assignCrmLeadsToCampaign(campaignId: string): Promise<any> {
  const url = campaignId
    ? `${API_BASE}/assign-crm-leads-to-campaign?campaign_id=${encodeURIComponent(campaignId)}`
    : `${API_BASE}/assign-crm-leads-to-campaign`;
  const res  = await fetch(url, { method: "POST" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Assign failed (${res.status})`);
  return data;
}

// ── Scraper scheduler start/stop ────────────────────────────────────
async function startScraper(intervalHours: number = 6): Promise<any> {
  const res  = await fetch(`${API_BASE}/scheduler/start?interval_hours=${intervalHours}`, { method: "POST" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Start failed (${res.status})`);
  return data;
}

async function stopScraper(): Promise<any> {
  const res  = await fetch(`${API_BASE}/scheduler/stop`, { method: "POST" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Stop failed (${res.status})`);
  return data;
}

// ── Fetch leads for the Push modal (server-side: tier + search + paging) ──
// Hits: GET /leads?tier=...&q=...&page=...&per_page=...
// Returns the full paginated payload:
//   { total, page, per_page, total_pages, has_next, has_prev, leads: [...] }
async function fetchAllLeads(opts: {
  tier?: string;
  q?: string;
  page?: number;
  per_page?: number;
} = {}): Promise<any> {
  const params = new URLSearchParams();
  if (opts.tier)               params.append("tier", opts.tier);
  if (opts.q && opts.q.trim()) params.append("q", opts.q.trim());
  params.append("page",     String(opts.page ?? 1));
  params.append("per_page", String(opts.per_page ?? 25));

  const res  = await fetch(`${API_BASE}/leads?${params}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Fetch failed (${res.status})`);
  return data;
}

// ── CSV Upload API ──────────────────────────────────────────────────
// FIX: responseType="blob" means xhr.responseText is unavailable.
// We always read the Blob with .text() and check content-type via includes().
async function uploadCSV(file: File, onProgress?: (progress: number) => void): Promise<any> {
  const formData = new FormData();
  formData.append("file", file);
  const xhr = new XMLHttpRequest();

  const promise = new Promise((resolve, reject) => {
    // Upload progress: cap at 90% — server processing takes remaining time
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.min((event.loaded / event.total) * 90, 90));
      }
    });

    xhr.addEventListener("load", () => {
      if (onProgress) onProgress(100);

      if (xhr.status === 200 || xhr.status === 201) {
        const contentType = xhr.getResponseHeader("content-type") ?? "";
        const isZip = contentType.includes("application/zip") || contentType.includes("octet-stream");

        // Read X-Process-Summary header for immediate stats
        let summary: any = null;
        try {
          const hdr = xhr.getResponseHeader("x-process-summary") ?? xhr.getResponseHeader("X-Process-Summary");
          if (hdr) summary = JSON.parse(hdr);
        } catch (_) { /* optional */ }

        const blob = xhr.response as Blob;
        if (isZip) {
          const url = window.URL.createObjectURL(blob);
          const a   = document.createElement("a");
          a.href     = url;
          a.download = "leads_output.zip";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          resolve({ success: true, message: "File downloaded successfully", summary });
        } else {
          // Non-zip: read blob as text then JSON-parse
          blob.text().then((text) => {
            try { resolve(JSON.parse(text)); }
            catch (_) { resolve({ success: true, raw: text }); }
          }).catch(() => resolve({ success: true }));
        }
      } else {
        // Error: read blob as text to get FastAPI detail message
        const blob = xhr.response as Blob;
        blob.text().then((text) => {
          try {
            const err = JSON.parse(text);
            reject(new Error(err.detail || `Upload failed (${xhr.status})`));
          } catch (_) {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        }).catch(() => reject(new Error(`Upload failed (${xhr.status})`)));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Network error — is the API server running at " + API_BASE + "?")));
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));
  });

  xhr.open("POST", `${API_BASE}/process-csv`);
  xhr.responseType = "blob";
  xhr.send(formData);
  return promise;
}

// ── Color tokens ──────────────────────────────────────────────────
const C = {
  pageBg:         "#F5F5F5",
  white:          "#ffffff",
  border:         "#e2e4ea",
  borderLight:    "#f0f0f0",
  textPrimary:    "#111827",
  textMuted:      "#6b7280",
  textHint:       "#9ca3af",
  headerGradient: "linear-gradient(90deg, #1a3565 0%, #1e4db7 55%, #1565C0 100%)",
  green:          "#2e7d32",
  greenLight:     "#e8f5e9",
  greenText:      "#1b5e20",
  red:            "#c62828",
  redLight:       "#fde8e8",
  amber:          "#f57f17",
  amberLight:     "#fff8e1",
  blue:           "#1a56a0",
  blueLight:      "#e3edf8",
  purple:         "#6a1b9a",
  purpleLight:    "#f3e5f5",
  // ICP source colors — matched exactly to primary_icp keys from DB
  srcLinkedIn:    "#ef6c00",   // LinkedinCreator
  srcGoogle:      "#1565C0",   // CourseCreator
  srcSubstack:    "#c62828",   // Substack
  srcPodcast:     "#2e7d32",   // Podcast
  srcConsultants: "#78909c",   // Consultants
};

// ─────────────────────────────────────────────────────────────────
// DERIVED DATA BUILDERS
// ─────────────────────────────────────────────────────────────────

/**
 * Build data_processing steps.
 * /dashboard meta:  { total_leads, with_email, pushed }
 * /leads/stats:     { by_tier: {"Tier 1": N, ...} }
 */
function buildDataProcessing(dashData: any, statsData: any) {
  const total     = dashData?.meta?.total_leads  ?? 0;
  const withEmail = dashData?.meta?.with_email   ?? 0;
  const pushed    = dashData?.meta?.pushed       ?? 0;
  // AI Scored = all leads that have a primary_icp assigned (from /leads/stats total)
  const aiScored  = statsData?.total             ?? total;
  // Tier 1 count comes from /leads/stats by_tier, not /dashboard
  const tier1     = statsData?.by_tier?.["Tier 1"] ?? 0;

  return [
    { label: "Raw Leads",  num: total,     sub: "Imported",     subBg: C.blueLight,   subC: C.blue      },
    { label: "AI Scored",  num: aiScored,  sub: "Classified",   subBg: "#f3e5f5",     subC: C.purple    },
    { label: "Enriched",   num: withEmail, sub: "Has Email",    subBg: C.amberLight,  subC: C.amber     },
    { label: "Tier 1",     num: tier1,     sub: "Top Leads",    subBg: C.greenLight,  subC: C.green     },
    { label: "Pushed",     num: pushed,    sub: "To Instantly", subBg: C.greenLight,  subC: C.greenText },
  ];
}

/**
 * Build lead_tiers.
 * /leads/stats by_tier has exact keys: "Tier 1", "Tier 2", "Tier 3", "Tier 4"
 * Note: AI pipeline converts Tier 4 → Tier 3, but pre-scored CSVs may have Tier 4.
 */
function buildLeadTiers(statsData: any, totalLeads: number) {
  const byTier = statsData?.by_tier ?? {};
  const t1     = byTier["Tier 1"] ?? 0;
  const t2     = byTier["Tier 2"] ?? 0;
  const t3     = byTier["Tier 3"] ?? 0;
  const t4     = byTier["Tier 4"] ?? 0;
  const total  = totalLeads || 1;
  const pct    = (n: number) => `${Math.round((n / total) * 100)}% of total`;

  const tiers = [
    {
      badge: "T1", title: "Tier 1 — Top Priority",
      headerBg: "#065D31", bodyBg: "#f0fdf4", borderColor: "#065D31",
      countColor: "#065D31", checkColor: "#15803d",
      convertBg: "#dcfce7", convertText: "#166534",
      count: t1, pct: pct(t1),
      checks: ["✓ ICP match", "✓ Apollo enriched", "✓ Pushed to Instantly"],
      convert: "Immediate outreach",
    },
    {
      badge: "T2", title: "Tier 2 — High Quality",
      headerBg: "#001F4F", bodyBg: "#eff6ff", borderColor: "#1e3a8a",
      countColor: "#1e3a8a", checkColor: "#1d4ed8",
      convertBg: "#dbeafe", convertText: "#1e40af",
      count: t2, pct: pct(t2),
      checks: ["✓ Good ICP fit", "✓ Has intent signals", "○ Needs nurturing"],
      convert: "Warm outreach",
    },
    {
      badge: "T3", title: "Tier 3 — Medium Quality",
      headerBg: "#774403", bodyBg: "#fffbeb", borderColor: "#b45309",
      countColor: "#92400e", checkColor: "#b45309",
      convertBg: "#fef3c7", convertText: "#92400e",
      count: t3, pct: pct(t3),
      checks: ["✓ Partial ICP fit", "○ Low intent", "○ Monitor only"],
      convert: "Long-term nurture",
    },
    {
      badge: "T4", title: "Tier 4 — Archive",
      headerBg: "#374151", bodyBg: "#f9fafb", borderColor: "#6b7280",
      countColor: "#6b7280", checkColor: "#9ca3af",
      convertBg: "#f3f4f6", convertText: "#6b7280",
      count: t4, pct: pct(t4),
      checks: ["○ Poor ICP fit", "○ No intent", "○ Do not contact"],
      convert: "Archived",
    },
  ];

  const hq_count = t1 + t2;
  const lq_count = t3 + t4;
  return {
    tiers,
    high_quality: `${hq_count} high-quality leads (Tier 1+2) ready for outreach`,
    low_quality:  `${lq_count} leads need further qualification`,
  };
}

// /**
//  * Build funnel from /dashboard meta + /leads/stats.
//  */
// function buildFunnel(dashData: any, statsData: any) {
//   const total  = dashData?.meta?.total_leads    ?? 0;
//   const t1     = statsData?.by_tier?.["Tier 1"] ?? 0;
//   const t2     = statsData?.by_tier?.["Tier 2"] ?? 0;
//   const email  = dashData?.meta?.with_email     ?? 0;
//   const pushed = dashData?.meta?.pushed         ?? 0;
//   const safe   = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : "0%");
//   const blues  = ["#1e3a8a", "#1e40af", "#1d4ed8", "#2563eb", "#3b82f6", "#60a5fa"];

//   return [
//     { label: "Discovered",  value: total,                        pct: "100%",                  bg: blues[0] },
//     { label: "AI Scored",   value: total,                        pct: safe(total, total),       bg: blues[1] },
//     { label: "Tier 1+2",    value: t1 + t2,                      pct: safe(t1 + t2, total),     bg: blues[2] },
//     { label: "Email Found", value: email,                         pct: safe(email, total),       bg: blues[3] },
//     { label: "Pushed",      value: pushed,                        pct: safe(pushed, total),      bg: blues[4] },
//     { label: "Converted",   value: Math.round(pushed * 0.08),    pct: safe(Math.round(pushed * 0.08), total), bg: blues[5] },
//   ];
// }

/**
 * Build funnel from /dashboard meta + /leads/stats.
 *
 * FIX: removed the fabricated "Converted" step (was Math.round(pushed * 0.08) —
 * an invented 8% conversion rate with no tracking behind it anywhere in the
 * backend). The funnel now ends at "Pushed", which is the last stage we can
 * actually verify. Add a real "Converted" step back once you have an actual
 * conversion event (Instantly reply/meeting-booked webhook, CRM deal stage, etc).
 */
function buildFunnel(dashData: any, statsData: any) {
  const total  = dashData?.meta?.total_leads    ?? 0;
  const t1     = statsData?.by_tier?.["Tier 1"] ?? 0;
  const t2     = statsData?.by_tier?.["Tier 2"] ?? 0;
  const email  = dashData?.meta?.with_email     ?? 0;
  const pushed = dashData?.meta?.pushed         ?? 0;
  const safe   = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : "0%");
  const blues  = ["#1e3a8a", "#1e40af", "#1d4ed8", "#2563eb", "#3b82f6"];

  return [
    { label: "Discovered",  value: total,   pct: "100%",              bg: blues[0] },
    { label: "AI Scored",   value: total,   pct: safe(total, total),  bg: blues[1] },
    { label: "Tier 1+2",    value: t1 + t2, pct: safe(t1 + t2, total), bg: blues[2] },
    { label: "Email Found", value: email,   pct: safe(email, total), bg: blues[3] },
    { label: "Pushed",      value: pushed,  pct: safe(pushed, total), bg: blues[4] },
    // "Converted" removed — no real conversion tracking exists yet.
  ];
}



// /**
//  * Build revenue panel.
//  */
// function buildRevenue(dashData: any, statsData: any) {
//   const t1     = statsData?.by_tier?.["Tier 1"] ?? 0;
//   const t2     = statsData?.by_tier?.["Tier 2"] ?? 0;
//   const total  = dashData?.meta?.total_leads    ?? 1;
//   const pushed = dashData?.meta?.pushed         ?? 0;

//   const makeSparkline = (base: number, variance: number, points = 12) =>
//     Array.from({ length: points }, (_, i) =>
//       Math.max(0, base + Math.sin(i * 0.8) * variance + i * (base * 0.05))
//     );

//   const paidRate = total ? Math.round(((t1 + t2) / total) * 100) : 0;

//   return {
//     total_signup:  `${total.toLocaleString()}`,
//     paid_rate:     `${paidRate}%`,
//     revenue_total: `$${((t1 + t2) * 200).toLocaleString()}`,
//     line1:         makeSparkline(8000, 4000),
//     line2:         makeSparkline(5000, 3000),
//   };
// }

/**
 * Build revenue panel.
 *
 * FIX: removed the fabricated $200/lead revenue formula and the fake sine-wave
 * sparkline (was decorative — not derived from any real data). Until you wire
 * in an actual payments/deals source, this panel shows only what's real:
 * total leads and the % that are Tier 1/2 ("Qualified %" — an honest label for
 * "AI-scored as high-tier," which is NOT the same as "converted" or "revenue").
 */
function buildRevenue(dashData: any, statsData: any) {
  const t1     = statsData?.by_tier?.["Tier 1"] ?? 0;
  const t2     = statsData?.by_tier?.["Tier 2"] ?? 0;
  const total  = dashData?.meta?.total_leads    ?? 1;

  const qualifiedPct = total ? Math.round(((t1 + t2) / total) * 100) : 0;

  return {
    total_signup: `${total.toLocaleString()}`,
    paid_rate:    `${qualifiedPct}%`,
    revenue_total: "—",   // no real revenue data source yet — see note above
    line1: [],            // sparkline removed — was synthetic (Math.sin), not real
    line2: [],
  };
}


// /**
//  * Build ICP performance table.
//  * /leads/stats by_icp keys: "LinkedinCreator", "CourseCreator", "Consultants", "Podcast", "Substack"
//  * Display names are mapped here for readability.
//  */
// function buildIcpPerformance(statsData: any, dashData: any) {
//   const byIcp = statsData?.by_icp ?? {};

//   // Map DB key → display label
//   const ICP_DISPLAY: Record<string, string> = {
//     LinkedinCreator: "LinkedIn Creator",
//     CourseCreator:   "Course Creator",
//     Consultants:     "Consultants",
//     Podcast:         "Podcast",
//     Substack:        "Substack",
//   };

//   const icpEntries = Object.entries(byIcp) as [string, number][];
//   if (!icpEntries.length) return [];

//   const total = icpEntries.reduce((sum, [, c]) => sum + (c as number), 0) || 1;

//   return icpEntries
//     .filter(([key]) => key && key !== "null" && key !== "undefined")
//     .map(([key, count]) => {
//       const displayName = ICP_DISPLAY[key] ?? key;
//       const pct         = Math.round(((count as number) / total) * 100);
//       const clickRate   = `${Math.min(pct + 5, 40)}%`;
//       const convRate    = `${Math.max(Math.round(pct * 0.3), 2)}%`;
//       const revenue     = `$${((count as number) * 200).toLocaleString()}`;

//       return {
//         name:      displayName,
//         leads:     (count as number).toLocaleString(),
//         clickRate,
//         convRate,
//         revenue,
//         status:    pct >= 20 ? "Active" : "Pending",
//         statusBg:  pct >= 20 ? C.greenLight : C.amberLight,
//         statusC:   pct >= 20 ? C.green      : C.amber,
//       };
//     });
// }

/**
 * Build ICP performance table.
 *
 * FIX: removed fabricated clickRate / convRate / revenue formulas
 * (were: min(pct+5,40)%, max(round(pct*0.3),2)%, count*200 — all arbitrary,
 * none backed by real click/conversion/revenue tracking). Now shows only
 * real counts, and marks untracked columns "—" — same honest pattern already
 * used in buildOutreachOverview() for Opened/Clicked/Replied/Bounced.
 */
function buildIcpPerformance(statsData: any, dashData: any) {
  const byIcp = statsData?.by_icp ?? {};

  const ICP_DISPLAY: Record<string, string> = {
    LinkedinCreator: "LinkedIn Creator",
    CourseCreator:   "Course Creator",
    Consultants:     "Consultants",
    Podcast:         "Podcast",
    Substack:        "Substack",
  };

  const icpEntries = Object.entries(byIcp) as [string, number][];
  if (!icpEntries.length) return [];

  return icpEntries
    .filter(([key]) => key && key !== "null" && key !== "undefined")
    .map(([key, count]) => {
      const displayName = ICP_DISPLAY[key] ?? key;

      return {
        name:      displayName,
        leads:     (count as number).toLocaleString(),
        clickRate: "—",   // requires Instantly link-click webhook data (not ingested yet)
        convRate:  "—",   // requires an actual conversion event (reply/booking/deal), not tracked yet
        revenue:   "—",   // requires real payments/deals data, not tracked yet
        status:    "—",   // no real campaign-state signal per ICP yet
        statusBg:  "#f3f4f6",
        statusC:   C.textMuted,
      };
    });
}

/**
 * Build alerts from scheduler + stats + dashboard.
 */
function buildAlerts(schedulerData: any, statsData: any, dashData: any) {
  const alerts = [];
  const t1     = statsData?.by_tier?.["Tier 1"]             ?? 0;
  const pushed = dashData?.meta?.pushed                     ?? 0;
  const remain = schedulerData?.auto_push_scheduler?.remaining_today ?? 0;

  alerts.push({
    num: 1, color: C.green,
    title: `${t1} Tier 1 leads ready for outreach`,
    sub:   `${pushed} already pushed to Instantly — ${remain} sends remaining today`,
  });

  const scraperRunning = schedulerData?.scraper_scheduler?.running;
  alerts.push({
    num: 2, color: scraperRunning ? C.blue : C.amber,
    title: scraperRunning ? "Scraper scheduler is active" : "Scraper scheduler is stopped",
    sub:   scraperRunning
      ? `Next run: ${schedulerData?.scraper_scheduler?.next_run ?? "soon"}`
      : "Start it via POST /scheduler/start",
  });

  const totalCycles = schedulerData?.scraper_scheduler?.total_cycles ?? 0;
  if (totalCycles > 0) {
    alerts.push({
      num: 3, color: C.purple,
      title: `${totalCycles} scraper cycles completed`,
      sub:   `${schedulerData?.scraper_scheduler?.total_leads ?? 0} leads discovered via scraper`,
    });
  }

  const sentToday  = schedulerData?.auto_push_scheduler?.sent_today  ?? 0;
  const dailyLimit = schedulerData?.auto_push_scheduler?.daily_limit ?? 300;
  if (sentToday >= dailyLimit * 0.9) {
    alerts.push({
      num: alerts.length + 1, color: C.red,
      title: "Daily send limit nearly reached",
      sub:   `${sentToday} / ${dailyLimit} sends used today`,
    });
  }

  while (alerts.length < 4) {
    alerts.push({
      num: alerts.length + 1, color: C.textHint,
      title: "No additional alerts",
      sub:   "All systems operating normally",
    });
  }

  return alerts.slice(0, 4);
}

/**
 * Build discovery sources bar chart.
 * Uses /leads/stats by_icp — keys exactly match DB values.
 * Color map keyed by DB primary_icp value.
 */
function buildDiscoverySources(statsData: any, scrapedStats: any) {
  const byIcp = statsData?.by_icp ?? {};

  // DB key → display info
  const ICP_SOURCE_MAP: Record<string, { label: string; color: string }> = {
    LinkedinCreator: { label: "LinkedIn",    color: C.srcLinkedIn    },
    CourseCreator:   { label: "Google",      color: C.srcGoogle      },
    Substack:        { label: "Substack",    color: C.srcSubstack    },
    Podcast:         { label: "Podcast",     color: C.srcPodcast     },
    Consultants:     { label: "Consultants", color: C.srcConsultants },
  };

  const validEntries = Object.entries(byIcp).filter(([k]) => k && k !== "null" && k !== "undefined") as [string, number][];
  const totalFromIcp = validEntries.reduce((s, [, v]) => s + (v as number), 0);
  const maxVal       = Math.max(...validEntries.map(([, v]) => v as number), 1);

  const bars = validEntries.map(([key, count]) => {
    const src       = ICP_SOURCE_MAP[key] ?? { label: key, color: C.srcConsultants };
    const heightPct = Math.round(((count as number) / maxVal) * 100);
    return { label: src.label, value: count as number, heightPct, color: src.color };
  });

  const scrapedTotal = scrapedStats?.total ?? 0;
  const grandTotal   = totalFromIcp + scrapedTotal;

  return { bars, total: grandTotal.toLocaleString() };
}

/**
 * Build lead scoring donut chart.
 * /leads/stats by_tier keys: "Tier 1", "Tier 2", "Tier 3", "Tier 4"
 */
function buildLeadScoring(statsData: any) {
  const byTier = statsData?.by_tier ?? {};
  const t1     = byTier["Tier 1"] ?? 0;
  const t2     = byTier["Tier 2"] ?? 0;
  const t3     = byTier["Tier 3"] ?? 0;
  const t4     = byTier["Tier 4"] ?? 0;
  const total  = t1 + t2 + t3 + t4 || 1;
  const p      = (n: number) => Math.round((n / total) * 100);

  const segments = [
    { label: "Hot",     value: t1, pct: p(t1), color: "#16a34a", intent: "High Intent",   intentBg: C.greenLight,  intentColor: C.green   },
    { label: "Warm",    value: t2, pct: p(t2), color: "#2563eb", intent: "Medium Intent", intentBg: C.blueLight,   intentColor: C.blue    },
    { label: "Cold",    value: t3, pct: p(t3), color: "#d97706", intent: "Low Intent",    intentBg: C.amberLight,  intentColor: C.amber   },
    { label: "Archive", value: t4, pct: p(t4), color: "#9ca3af", intent: "No Intent",     intentBg: "#f3f4f6",     intentColor: C.textMuted },
  ];

  return { segments, total: t1 + t2 + t3 + t4 };
}

/**
 * Build outreach overview cards.
 * /dashboard meta: { pushed, with_email, total_leads }
 * /leads/stats by_tier + by_icp
 */
function buildOutreachOverview(dashData: any, statsData: any) {
  const pushed  = dashData?.meta?.pushed         ?? 0;
  const t1      = statsData?.by_tier?.["Tier 1"] ?? 0;
  const email   = dashData?.meta?.with_email     ?? 0;
  const total   = dashData?.meta?.total_leads    ?? 1;

  // Real email outreach stats — open/click/reply/bounce come from Instantly webhooks,
  // not stored in our DB yet. Show real pushed count; mark untracked metrics as "—"
  const byIcp = statsData?.by_icp ?? {};
  const linkedinCount    = byIcp["LinkedinCreator"] ?? 0;
  const consultantCount  = byIcp["Consultants"]     ?? 0;
  const podcastCount     = byIcp["Podcast"]         ?? 0;
  const substackCount    = byIcp["Substack"]        ?? 0;
  const courseCount      = byIcp["CourseCreator"]   ?? 0;

  return [
    {
      title: "Email Outreach", icon: "✉", sent: pushed.toLocaleString(),
      headerBg: "#1e4db7", bodyBg: "#eff6ff", borderColor: "#1e3a8a",
      rows: [
        { label: "Opened",  val: "—", pct: "—" },
        { label: "Clicked", val: "—", pct: "—" },
        { label: "Replied", val: "—", pct: "—" },
        { label: "Bounced", val: "—", pct: "—" },
      ],
    },
    {
      title: "Apollo Enrichment", icon: "🔍", sent: email.toLocaleString(),
      headerBg: "#065D31", bodyBg: "#f0fdf4", borderColor: "#065D31",
      rows: [
        { label: "Emails Found", val: email.toLocaleString(),                         pct: total ? `${Math.round((email / total) * 100)}%`   : "0%" },
        { label: "Tier 1 Ready", val: t1.toLocaleString(),                            pct: email ? `${Math.round((t1 / email) * 100)}%`      : "0%" },
        { label: "Pushed",       val: pushed.toLocaleString(),                         pct: email ? `${Math.round((pushed / email) * 100)}%`  : "0%" },
        { label: "Pending",      val: Math.max(0, t1 - pushed).toLocaleString(),      pct: t1    ? `${Math.round((Math.max(0, t1 - pushed) / t1) * 100)}%` : "0%" },
      ],
    },
    {
      title: "LinkedIn Discovery", icon: "🔗", sent: linkedinCount.toLocaleString(),
      headerBg: "#ef6c00", bodyBg: "#fff3e0", borderColor: "#e65100",
      rows: [
        { label: "LinkedIn Creators", val: linkedinCount.toLocaleString(),   pct: "—" },
        { label: "Consultants",        val: consultantCount.toLocaleString(), pct: "—" },
        { label: "Podcasters",         val: podcastCount.toLocaleString(),    pct: "—" },
        { label: "Substackers",        val: substackCount.toLocaleString(),   pct: "—" },
      ],
    },
    {
      title: "Scraper Pipeline", icon: "⚙", sent: courseCount.toLocaleString(),
      headerBg: "#6a1b9a", bodyBg: "#f3e5f5", borderColor: "#6a1b9a",
      rows: [
        { label: "Course Creators", val: courseCount.toLocaleString(),   pct: "—" },
        { label: "Substack",        val: substackCount.toLocaleString(), pct: "—" },
        { label: "YouTube",         val: "—",                            pct: "—" },
        { label: "Podcast",         val: podcastCount.toLocaleString(),  pct: "—" },
      ],
    },
  ];
}

/**
 * Build sources breakdown table.
 * Distributes leads per ICP proportionally across tiers.
 * Uses correct DB keys for both by_icp and by_tier.
 */
function buildSourcesBreakdown(statsData: any) {
  const byIcp  = statsData?.by_icp  ?? {};
  const byTier = statsData?.by_tier ?? {};
  const total  = statsData?.total   ?? 0;

  const sources = [
    { src: "LinkedIn",    icp: "LinkedinCreator" },
    { src: "Google",      icp: "CourseCreator"   },
    { src: "Substack",    icp: "Substack"        },
    { src: "Podcast",     icp: "Podcast"         },
    { src: "Consultants", icp: "Consultants"     },
  ];

  const tierBg: Record<string, { bg: string; c: string }> = {
    t1: { bg: "#dcfce7", c: "#166534" },
    t2: { bg: "#dbeafe", c: "#1e40af" },
    t3: { bg: "#fef3c7", c: "#92400e" },
    t4: { bg: "#f3f4f6", c: "#6b7280" },
  };

  const colorMap: Record<string, string> = {
    LinkedIn:    C.srcLinkedIn,
    Google:      C.srcGoogle,
    Substack:    C.srcSubstack,
    Podcast:     C.srcPodcast,
    Consultants: C.srcConsultants,
  };

  const t1w = byTier["Tier 1"] ?? 0;
  const t2w = byTier["Tier 2"] ?? 0;
  const t3w = byTier["Tier 3"] ?? 0;
  const t4w = byTier["Tier 4"] ?? 0;
  const tw  = (t1w + t2w + t3w + t4w) || 1;

  const rows = sources.map((s) => {
    const leads = byIcp[s.icp] ?? 0;
    const t1v   = Math.round((t1w / tw) * leads);
    const t2v   = Math.round((t2w / tw) * leads);
    const t3v   = Math.round((t3w / tw) * leads);
    const t4v   = Math.max(0, leads - t1v - t2v - t3v);

    return {
      src:   s.src,
      leads: leads.toLocaleString(),
      color: colorMap[s.src] ?? C.srcConsultants,
      t1:    { value: t1v, ...tierBg.t1 },
      t2:    { value: t2v, ...tierBg.t2 },
      t3:    { value: t3v, ...tierBg.t3 },
      t4:    { value: t4v, ...tierBg.t4 },
    };
  });

  return {
    rows,
    grand_total: total.toLocaleString(),
    totals: [
      { val: t1w, ...tierBg.t1 },
      { val: t2w, ...tierBg.t2 },
      { val: t3w, ...tierBg.t3 },
      { val: Math.max(0, t4w), ...tierBg.t4 },
    ],
  };
}

/**
 * Build sender metrics from lead records.
 * Lead fields from /leads: full_name, primary_icp, priority_tier,
 *   company, industry, instantly_pushed_at, fit_score, email, linkedin_url
 */
function buildSenderMetrics(leadsData: any) {
  const leads: any[] = leadsData?.leads ?? [];
  if (!leads.length) return [];

  return leads.slice(0, 8).map((lead: any) => {
    const isPushed = !!lead.instantly_pushed_at;
    return {
      // "Source" = industry or company for the sender row
      source:      lead.industry || lead.company || "—",
      // "ICP Name" = human-readable ICP label
      icp:         lead.primary_icp
                     ? (lead.primary_icp === "LinkedinCreator" ? "LinkedIn Creator"
                     : lead.primary_icp === "CourseCreator"    ? "Course Creator"
                     : lead.primary_icp)
                     : "—",
      // Sent = 1 if pushed to Instantly, else 0
      sent:        isPushed ? "1" : "0",
      open:        isPushed ? "—" : "0",   // open tracking not in DB yet
      click:       "—",
      bounce:      "0%",
      bounceLevel: "Low",
    };
  });
}

// ─────────────────────────────────────────────────────────────────
// DATE RANGE PICKER UTILS
// ─────────────────────────────────────────────────────────────────
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function todayDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function fmtDate(d: Date | null): string {
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
}

const PRESETS: { id: PresetId; label: string; fn?: () => [Date, Date] }[] = [
  { id: "today",     label: "Today",         fn: () => { const d = todayDate(); return [d, d]; } },
  { id: "yesterday", label: "Yesterday",     fn: () => { const d = new Date(todayDate()); d.setDate(d.getDate()-1); return [d,d]; } },
  { id: "last7",     label: "Last 7 days",   fn: () => { const e = todayDate(), s = new Date(e); s.setDate(s.getDate()-6); return [s,e]; } },
  { id: "last30",    label: "Last 30 days",  fn: () => { const e = todayDate(), s = new Date(e); s.setDate(s.getDate()-29); return [s,e]; } },
  { id: "thisMonth", label: "This month",    fn: () => { const d = todayDate(); return [new Date(d.getFullYear(),d.getMonth(),1), new Date(d.getFullYear(),d.getMonth()+1,0)]; } },
  { id: "lastMonth", label: "Last month",    fn: () => { const d = todayDate(), m = d.getMonth()-1, y = m<0?d.getFullYear()-1:d.getFullYear(), mm = m<0?11:m; return [new Date(y,mm,1), new Date(y,mm+1,0)]; } },
  { id: "last3m",    label: "Last 3 months", fn: () => { const e = todayDate(), s = new Date(e); s.setMonth(s.getMonth()-3); return [s,e]; } },
  { id: "custom",    label: "Custom range"   },
];

// ─────────────────────────────────────────────────────────────────
// CSV UPLOAD MODAL COMPONENT
// ─────────────────────────────────────────────────────────────────
interface CSVUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
}

function CSVUploadModal({ open, onClose, onUploadComplete }: CSVUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);
  const [uploadSummary, setUploadSummary] = useState<any>(null); // FIX: show X-Process-Summary stats
  const fileInputRef = useRef<HTMLInputElement>(null);

  // FIX: progress label — "Uploading" until 90%, "Processing on server…" from 90%–99%, "Done" at 100%
  const progressLabel = progress < 90
    ? `Uploading… ${Math.round(progress)}%`
    : progress < 100
    ? `Processing leads on server… (this may take 2–4 min for large files)`
    : `Done!`;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".csv") && !file.type.includes("csv")) {
        setError("Please select a valid CSV file"); setSelectedFile(null); return;
      }
      setSelectedFile(file); setError(null); setSuccess(null); setUploadSummary(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError("Please select a file first"); return; }
    setUploading(true); setProgress(0); setError(null); setSuccess(null); setUploadSummary(null);
    try {
      const result = await uploadCSV(selectedFile, (prog) => setProgress(prog));
      // FIX: read summary from X-Process-Summary header (returned by our API)
      const summary = result?.summary ?? null;
      if (summary) setUploadSummary(summary);
      const msg = summary
        ? `✅ Done! ${summary.total ?? "?"} leads processed — Tier1: ${summary.tier1 ?? 0}, Tier2: ${summary.tier2 ?? 0}, Pushed: ${summary.pushed ?? 0}. ZIP downloading…`
        : "✅ File processed! Check your downloads folder for the results ZIP.";
      setSuccess(msg);
      if (onUploadComplete) setTimeout(() => onUploadComplete(), 1500);
      setTimeout(() => handleClose(), 4000);
    } catch (err: any) {
      setError(err.message || "Upload failed");
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null); setProgress(0); setError(null); setSuccess(null); setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop     = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith(".csv") || file.type.includes("csv"))) {
      setSelectedFile(file); setError(null); setSuccess(null);
    } else {
      setError("Please drop a valid CSV file");
    }
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="csv-upload-modal">
      <Box sx={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:500, maxWidth:"90vw", bgcolor:C.white, borderRadius:"12px", boxShadow:"0 24px 48px rgba(0,0,0,0.2)", outline:"none" }}>
        <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", p:"16px 20px", borderBottom:`1px solid ${C.borderLight}` }}>
          <Typography fontSize={18} fontWeight={600} color={C.textPrimary}>Upload CSV File</Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p:"20px" }}>
          <Box onDragOver={handleDragOver} onDrop={handleDrop}
            sx={{ border:`2px dashed ${selectedFile ? C.blue : C.border}`, borderRadius:"8px", p:"32px 20px", textAlign:"center", cursor:"pointer", bgcolor:selectedFile ? C.blueLight : "#fafafa", transition:"all 0.2s", "&:hover":{ borderColor:C.blue, bgcolor:C.blueLight } }}
            onClick={() => fileInputRef.current?.click()}>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display:"none" }} />
            <CloudUploadIcon sx={{ fontSize:48, color:C.blue, mb:1 }} />
            <Typography fontSize={14} color={C.textPrimary} mb={0.5}>{selectedFile ? selectedFile.name : "Click or drag CSV file here"}</Typography>
            <Typography fontSize={12} color={C.textMuted}>Supported format: CSV (max 100MB)</Typography>
          </Box>
          {uploading && (
            <Box sx={{ mt:2 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height:6, borderRadius:3 }} />
              <Typography fontSize={11} color={progress >= 90 ? C.amber : C.textMuted} mt={0.5} fontWeight={progress >= 90 ? 600 : 400}>
                {progressLabel}
              </Typography>
            </Box>
          )}
          {error   && <Alert severity="error"   sx={{ mt:2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt:2 }}>{success}</Alert>}
          {uploadSummary && !uploading && (
            <Box sx={{ mt:1.5, p:"10px 12px", background:C.greenLight, borderRadius:"6px", border:`1px solid #a7f3d0` }}>
              <Typography fontSize={11} fontWeight={700} color={C.greenText} mb={0.5}>📊 Processing Summary</Typography>
              <Box sx={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"8px" }}>
                {[
                  { label:"Total", val: uploadSummary.total ?? 0 },
                  { label:"Tier 1", val: uploadSummary.tier1 ?? 0 },
                  { label:"Tier 2", val: uploadSummary.tier2 ?? 0 },
                  { label:"Pushed", val: uploadSummary.pushed ?? 0 },
                ].map((s) => (
                  <Box key={s.label} sx={{ textAlign:"center", background:C.white, borderRadius:"4px", p:"6px" }}>
                    <Typography fontSize={16} fontWeight={700} color={C.textPrimary}>{s.val}</Typography>
                    <Typography fontSize={9.5} color={C.textMuted}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ mt:2, p:"10px", bgcolor:"#f8f9ff", borderRadius:"6px", border:`1px solid ${C.borderLight}` }}>
            <Typography fontSize={11} color={C.textMuted} lineHeight={1.4}>
              <strong>Expected CSV columns:</strong> name, current_title, current_employer, industry, location, linkedin_url, email (optional)<br /><br />
              The system will:<br />
              1. Score leads using AI (ICP classification → LinkedinCreator / CourseCreator / Consultants / Podcast / Substack)<br />
              2. Assign priority tier (Tier 1 / Tier 2 / Tier 3)<br />
              3. Push Tier 1 leads to Instantly<br />
              4. Return a ZIP with enriched data
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", gap:1, p:"16px 20px", borderTop:`1px solid ${C.borderLight}` }}>
          <Button onClick={handleClose} disabled={uploading} sx={{ textTransform:"none", fontSize:13, color:C.textMuted, border:`1px solid ${C.border}`, borderRadius:"6px", px:"16px", py:"6px" }}>Cancel</Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading} sx={{ textTransform:"none", fontSize:13, fontWeight:500, color:"#fff", background:C.blue, borderRadius:"6px", px:"20px", py:"6px", "&:hover":{ background:"#1a3565" }, "&:disabled":{ opacity:0.5 } }}>
            {uploading ? "Uploading..." : "Upload & Process"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// DATE RANGE PICKER COMPONENT
// ─────────────────────────────────────────────────────────────────
function DateRangePicker({ onApply, onClose }: { onApply: (range: DateRange, label: string) => void; onClose: () => void }) {
  const now = todayDate();
  const initRange = PRESETS.find((p) => p.id === "last30")!.fn!();
  const [start,   setStart]   = useState<Date | null>(initRange[0]);
  const [end,     setEnd]     = useState<Date | null>(initRange[1]);
  const [hover,   setHover]   = useState<Date | null>(null);
  const [step,    setStep]    = useState<0|1|2>(0);
  const [activeP, setActiveP] = useState<PresetId>("last30");
  const [viewY,   setViewY]   = useState(initRange[0].getFullYear());
  const [viewM,   setViewM]   = useState(initRange[0].getMonth());

  const selectPreset = (p: (typeof PRESETS)[0]) => {
    setActiveP(p.id);
    if (p.fn) { const [s,e] = p.fn(); setStart(s); setEnd(e); setStep(0); setViewY(s.getFullYear()); setViewM(s.getMonth()); }
    else { setStart(null); setEnd(null); setStep(1); }
  };

  const clickDay = (d: Date) => {
    if (step === 0 || (step === 2 && end)) setStep(1);
    if (step === 1) { setStart(d); setEnd(null); setHover(null); setStep(2); setActiveP("custom"); }
    else if (step === 2) {
      if (d < start!) { setEnd(start); setStart(d); } else { setEnd(d); }
      setHover(null); setStep(0);
    }
  };

  const effEnd = step === 2 ? hover ?? null : end;

  const renderCells = () => {
    const firstDay    = new Date(viewY, viewM, 1).getDay();
    const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
    const daysInPrev  = new Date(viewY, viewM, 0).getDate();

    return Array.from({ length: 42 }, (_, i) => {
      let d: Date, isOther = false;
      if (i < firstDay)                   { d = new Date(viewY, viewM-1, daysInPrev-(firstDay-1-i)); isOther = true; }
      else if (i-firstDay >= daysInMonth) { d = new Date(viewY, viewM+1, i-firstDay-daysInMonth+1); isOther = true; }
      else                                { d = new Date(viewY, viewM, i-firstDay+1); }

      const isStart = sameDay(d, start), isEnd = sameDay(d, effEnd);
      const inRange = !!start && !!effEnd && d > start && d < effEnd;
      const isToday = sameDay(d, now);

      let bg = "transparent", color = isOther ? C.textHint : C.textPrimary, br = "6px";
      if (isStart || isEnd) { bg = "#1e4db7"; color = "#fff"; br = isStart ? "6px 0 0 6px" : "0 6px 6px 0"; }
      if (isStart && isEnd) br = "6px";
      if (inRange)          { bg = "#dbeafe"; color = "#1e40af"; br = "0"; }

      return (
        <Box key={i} onClick={() => clickDay(d)} onMouseEnter={() => step === 2 && setHover(d)}
          sx={{ width:32, height:32, lineHeight:"32px", textAlign:"center", borderRadius:br, fontSize:12, cursor:"pointer", color, background:bg, border:isToday?"1px solid #1e4db7":"none", fontWeight:isToday?700:400, userSelect:"none", "&:hover":{ filter:"brightness(0.92)" } }}>
          {d.getDate()}
        </Box>
      );
    });
  };

  const rangeLabel = start && end ? `${fmtDate(start)} – ${fmtDate(end)}` : start && step===2 ? `${fmtDate(start)} – pick end date` : "Select a date range";

  return (
    <Box sx={{ position:"absolute", top:"calc(100% + 8px)", left:0, zIndex:1400, background:C.white, border:`1px solid ${C.border}`, borderRadius:"10px", boxShadow:"0 8px 24px rgba(0,0,0,0.13)", p:"16px", width:680 }} onClick={(e) => e.stopPropagation()}>
      <Box sx={{ display:"grid", gridTemplateColumns:"180px 1fr", gap:"16px" }}>
        <Box>
          <Typography fontSize={11} fontWeight={600} color={C.textMuted} mb={1} sx={{ textTransform:"uppercase", letterSpacing:0.5 }}>Quick select</Typography>
          <Stack spacing={0.4}>
            {PRESETS.map((p) => (
              <Box key={p.id} onClick={() => selectPreset(p)} sx={{ px:"10px", py:"7px", borderRadius:"6px", fontSize:12, cursor:"pointer", background:activeP===p.id?"#dbeafe":"transparent", color:activeP===p.id?"#1e40af":C.textPrimary, border:activeP===p.id?"0.5px solid #93c5fd":"0.5px solid transparent", "&:hover":{ background:activeP===p.id?"#dbeafe":"#f3f4f6" } }}>
                {p.label}
              </Box>
            ))}
          </Stack>
        </Box>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Box onClick={() => { let m=viewM-1,y=viewY; if(m<0){m=11;y--;} setViewM(m);setViewY(y); }} sx={{ border:`0.5px solid ${C.border}`, borderRadius:"6px", px:"10px", py:"3px", cursor:"pointer", fontSize:18, color:C.textPrimary, "&:hover":{ background:"#f3f4f6" } }}>‹</Box>
            <Typography fontSize={13} fontWeight={600} color={C.textPrimary}>{MONTHS[viewM]} {viewY}</Typography>
            <Box onClick={() => { let m=viewM+1,y=viewY; if(m>11){m=0;y++;} setViewM(m);setViewY(y); }} sx={{ border:`0.5px solid ${C.border}`, borderRadius:"6px", px:"10px", py:"3px", cursor:"pointer", fontSize:18, color:C.textPrimary, "&:hover":{ background:"#f3f4f6" } }}>›</Box>
          </Stack>
          <Box sx={{ display:"grid", gridTemplateColumns:"repeat(7, 32px)", gap:"2px", mb:"4px" }}>
            {DAYS.map((d) => <Box key={d} sx={{ fontSize:11, fontWeight:500, color:C.textHint, textAlign:"center", pb:"4px" }}>{d}</Box>)}
          </Box>
          <Box sx={{ display:"grid", gridTemplateColumns:"repeat(7, 32px)", gap:"2px" }} onMouseLeave={() => step===2 && setHover(null)}>
            {renderCells()}
          </Box>
          <Box sx={{ mt:1.5, pt:1.5, borderTop:`1px solid ${C.borderLight}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <Typography fontSize={12} color={C.textMuted}>{rangeLabel}</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onClose} sx={{ textTransform:"none", fontSize:12, color:C.textMuted, border:`0.5px solid ${C.border}`, borderRadius:"6px", px:"14px" }}>Cancel</Button>
              <Button size="small" onClick={() => { if(start && end) { const p = PRESETS.find((x) => x.id===activeP); onApply({start,end}, p?.id!=="custom" ? p!.label : "Custom range"); } }} disabled={!start||!end} sx={{ textTransform:"none", fontSize:12, color:"#fff", background:"#1e4db7", borderRadius:"6px", px:"14px", "&:hover":{ background:"#1a3565" }, "&:disabled":{ opacity:0.45, color:"#fff" } }}>Apply</Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// CAMPAIGN CONTROL MODAL
// ─────────────────────────────────────────────────────────────────
interface CampaignModalProps {
  open: boolean;
  onClose: () => void;
  campaigns: any[];
  loadingCampaigns: boolean;
  onToggle: (campaignId: string, currentStatus: number) => Promise<void>;
  togglingId: string | null;
}

function CampaignModal({ open, onClose, campaigns, loadingCampaigns, onToggle, togglingId }: CampaignModalProps) {
  const statusMap: Record<number, { label: string; bg: string; color: string }> = {
    1: { label: "Active",    bg: C.greenLight,  color: C.green     },
    2: { label: "Paused",    bg: C.amberLight,  color: C.amber     },
    0: { label: "Draft",     bg: "#f3f4f6",     color: C.textMuted },
    3: { label: "Completed", bg: C.blueLight,   color: C.blue      },
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:600, maxWidth:"94vw", bgcolor:C.white, borderRadius:"12px", boxShadow:"0 24px 48px rgba(0,0,0,0.2)", outline:"none" }}>
        <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", p:"16px 20px", borderBottom:`1px solid ${C.borderLight}` }}>
          <Typography fontSize={18} fontWeight={600} color={C.textPrimary}>Campaign Control</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p:"20px", maxHeight:"60vh", overflowY:"auto" }}>
          {loadingCampaigns && <Typography fontSize={12} color={C.textMuted} textAlign="center" py={3}>Loading campaigns…</Typography>}
          {!loadingCampaigns && campaigns.length === 0 && <Typography fontSize={12} color={C.textMuted} textAlign="center" py={3}>No campaigns found.</Typography>}
          <Stack spacing={1.5}>
            {campaigns.map((c: any) => {
              const statusNum  = Number(c.status);
              const st         = statusMap[statusNum] ?? { label: "Unknown", bg: "#f3f4f6", color: C.textMuted };
              const isActive   = statusNum === 1;
              const canToggle  = statusNum === 1 || statusNum === 2;
              const isToggling = togglingId === c.id;
              return (
                <Box key={c.id} sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", p:"12px 14px", border:`1px solid ${C.border}`, borderRadius:"8px", background:"#fafafa" }}>
                  <Box sx={{ flex:1, minWidth:0 }}>
                    <Typography fontSize={13} fontWeight={600} color={C.textPrimary} noWrap>{c.name || c.id}</Typography>
                    <Typography fontSize={11} color={C.textMuted} noWrap>{c.id}</Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink:0, ml:2 }}>
                    <Box sx={{ background:st.bg, color:st.color, fontSize:10.5, fontWeight:700, px:"8px", py:"3px", borderRadius:"4px", whiteSpace:"nowrap" }}>{st.label}</Box>
                    {canToggle && (
                      <Button size="small" disabled={isToggling} onClick={() => onToggle(c.id, statusNum)}
                        sx={{ textTransform:"none", fontSize:12, fontWeight:600, px:"14px", py:"5px", borderRadius:"6px", minWidth:90, color:"#fff",
                          background: isActive ? "#c62828" : "#2e7d32",
                          "&:hover": { background: isActive ? "#b71c1c" : "#1b5e20" },
                          "&:disabled": { opacity:0.55, color:"#fff" } }}>
                        {isToggling ? "…" : isActive ? "⏸ Pause" : "▶ Resume"}
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <Box sx={{ p:"12px 20px", borderTop:`1px solid ${C.borderLight}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <AssignCrmButton campaigns={campaigns} />
          <Button onClick={onClose} sx={{ textTransform:"none", fontSize:13, color:C.textMuted, border:`1px solid ${C.border}`, borderRadius:"6px", px:"16px", py:"6px" }}>Close</Button>
        </Box>
      </Box>
    </Modal>
  );
}

// ── Assign CRM Leads to Campaign button (used inside CampaignModal) ──────────
function AssignCrmButton({ campaigns }: { campaigns: any[] }) {
  const [assigning, setAssigning] = useState(false);
  const [result,    setResult]    = useState<string | null>(null);
  const [selCamp,   setSelCamp]   = useState("");

  const doAssign = async () => {
    setAssigning(true); setResult(null);
    try {
      const res = await assignCrmLeadsToCampaign(selCamp);
      setResult(`Assigned ${res.pushed ?? 0} leads → campaign${selCamp ? ` ${selCamp.slice(0,8)}…` : " (ICP routing)"}. Errors: ${res.errors ?? 0}`);
    } catch (e: any) {
      setResult(`Error: ${e.message}`);
    } finally { setAssigning(false); }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {campaigns.length > 0 && (
        <select value={selCamp} onChange={(e) => setSelCamp(e.target.value)}
          style={{ fontSize:11, padding:"4px 8px", borderRadius:5, border:`1px solid #d1d5db`, color:"#374151", background:"#fff", outline:"none" }}>
          <option value="">ICP-based routing</option>
          {campaigns.map((c: any) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
        </select>
      )}
      <Button size="small" onClick={doAssign} disabled={assigning}
        sx={{ textTransform:"none", fontSize:11.5, fontWeight:600, color:"#fff", background:"#6a1b9a", borderRadius:"6px", px:"12px", py:"5px",
          "&:hover":{ background:"#4a148c" }, "&:disabled":{ opacity:0.5, color:"#fff" } }}>
        {assigning ? "Assigning…" : "📤 Assign CRM → Campaign"}
      </Button>
      {result && <Typography fontSize={10.5} color={C.textMuted} sx={{ maxWidth:200 }}>{result}</Typography>}
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────────────────────────────
function Header({ onDateChange, onUploadClick, onExportClick, onPushClick, onScraperClick }: {
  onDateChange: (range: DateRange) => void;
  onUploadClick: () => void;
  onExportClick: () => void;
  onPushClick?: () => void;
  onScraperClick?: () => void;
}) {
  const [calOpen,   setCalOpen]   = useState(false);
  const [dateLabel, setDateLabel] = useState("Last 30 Days");
  const [subLabel,  setSubLabel]  = useState("All time");
  const pickerRef = useRef<HTMLDivElement>(null);

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaigns,         setCampaigns]         = useState<any[]>([]);
  const [loadingCampaigns,  setLoadingCampaigns]  = useState(false);
  const [togglingId,        setTogglingId]        = useState<string | null>(null);
  const [campaignError,     setCampaignError]     = useState<string | null>(null);

  const hasActive = campaigns.some((c) => Number(c.status) === 1);
  const hasPaused = campaigns.some((c) => Number(c.status) === 2);

  const loadCampaigns = async () => {
    setLoadingCampaigns(true); setCampaignError(null);
    try {
      const data = await fetchCampaigns();
      setCampaigns(data.campaigns ?? []);
    } catch (e: any) {
      setCampaignError(e.message);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const openCampaignModal = () => { setCampaignModalOpen(true); loadCampaigns(); };

  const handleToggleCampaign = async (campaignId: string, currentStatus: number) => {
    setTogglingId(campaignId); setCampaignError(null);
    try {
      if (Number(currentStatus) === 1) await pauseCampaign(campaignId);
      else await resumeCampaign(campaignId);
      const newStatus = Number(currentStatus) === 1 ? 2 : 1;
      setCampaigns((prev) => prev.map((c) => c.id === campaignId ? { ...c, status: newStatus } : c));
    } catch (e: any) {
      setCampaignError(e.message);
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setCalOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleApply = (range: DateRange, label: string) => {
    setDateLabel(label);
    setSubLabel(`${fmtDate(range.start)} – ${fmtDate(range.end)}`);
    setCalOpen(false);
    onDateChange(range);
  };

  return (
    <>
      <CampaignModal open={campaignModalOpen} onClose={() => { setCampaignModalOpen(false); setCampaignError(null); }}
        campaigns={campaigns} loadingCampaigns={loadingCampaigns}
        onToggle={handleToggleCampaign} togglingId={togglingId} />
      <Box sx={{ background:C.headerGradient, color:"#fff", px:"28px", py:"12px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:"6px", px:"10px", py:"5px", fontSize:12, display:"inline-flex", alignItems:"center", gap:"6px", cursor:"pointer" }}>
            ← CodeGenie
          </Box>
          <Box ref={pickerRef} sx={{ position:"relative" }}>
            <Box onClick={() => setCalOpen((o) => !o)} sx={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:"6px", px:"10px", py:"5px", fontSize:12, display:"inline-flex", alignItems:"center", gap:"6px", cursor:"pointer", mb:"2px", userSelect:"none" }}>
              📅 {dateLabel} ▾
            </Box>
            <Typography fontSize={11} sx={{ opacity:0.65, pl:"4px" }}>{subLabel}</Typography>
            {calOpen && <DateRangePicker onApply={handleApply} onClose={() => setCalOpen(false)} />}
          </Box>
        </Stack>
        <Box sx={{ textAlign:"center" }}>
          <Typography fontSize={19} fontWeight={700} lineHeight={1.25}>Customer Acquisition Dashboard</Typography>
          <Typography fontSize={11.5} sx={{ opacity:0.78 }}>Overview of Discovery, Lead Quality &amp; Outreach Performance</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width:34, height:34, borderRadius:"50%", background:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, cursor:"pointer" }}>🔔</Box>
          <Button size="small" onClick={onUploadClick} sx={{ textTransform:"none", fontSize:11.5, fontWeight:500, color:"#fff", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.35)", borderRadius:"6px", px:"12px", py:"5px", whiteSpace:"nowrap" }}>Upload CSV</Button>
          <Button size="small" onClick={onExportClick} sx={{ textTransform:"none", fontSize:11.5, fontWeight:500, color:"#fff", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.35)", borderRadius:"6px", px:"12px", py:"5px", whiteSpace:"nowrap" }}>⬇ Export Report</Button>
          <Button size="small" onClick={openCampaignModal}
            sx={{ textTransform:"none", fontSize:11.5, fontWeight:500, color:"#fff",
              background: hasActive ? "rgba(198,40,40,0.75)" : hasPaused ? "rgba(46,125,50,0.75)" : "rgba(255,255,255,0.1)",
              border:"1px solid rgba(255,255,255,0.35)", borderRadius:"6px", px:"12px", py:"5px", whiteSpace:"nowrap",
              "&:hover": { background: hasActive ? "rgba(183,28,28,0.9)" : "rgba(27,94,32,0.9)" } }}>
            {hasActive ? "⏸ Pause Campaign" : hasPaused ? "▶ Resume Campaign" : "⚙ Campaigns"}
          </Button>
          {campaignError && (
            <Typography fontSize={10.5} sx={{ color:"#ffcdd2", maxWidth:180, lineHeight:1.3 }}>{campaignError}</Typography>
          )}
          <Button size="small" onClick={onPushClick}
            sx={{ textTransform:"none", fontSize:11.5, fontWeight:600, color:"#fff", background:"#2e7d32", borderRadius:"6px", px:"12px", py:"5px", whiteSpace:"nowrap",
              "&:hover":{ background:"#1b5e20" } }}>
            🚀 Push to Instantly
          </Button>
          <Button size="small" onClick={onScraperClick}
            sx={{ textTransform:"none", fontSize:11.5, fontWeight:500, color:"#fff", background:"rgba(255,255,255,0.13)", border:"1px solid rgba(255,255,255,0.35)", borderRadius:"6px", px:"12px", py:"5px", whiteSpace:"nowrap",
              "&:hover":{ background:"rgba(255,255,255,0.22)" } }}>
            ⚙ Scraper
          </Button>
        </Stack>
      </Box>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// KPI METRICS BAR
// ─────────────────────────────────────────────────────────────────
function KpiMetricsBar({ metrics }: { metrics: any[] }) {
  return (
    <Box sx={{ background:C.white, borderBottom:`1px solid ${C.border}` }}>
      <Box sx={{ px:"20px", py:"7px", borderBottom:`1px solid ${C.borderLight}` }}>
        <Typography fontSize={11} fontWeight={700} color={C.textMuted} letterSpacing={0.5}>KPI Metrics</Typography>
      </Box>
      <Box sx={{ display:"grid", gridTemplateColumns:`repeat(${metrics.length}, 1fr)` }}>
        {metrics.map((m: any, i: number) => (
          <Box key={i} sx={{ p:"10px 12px", borderRight:i < metrics.length-1 ? `1px solid ${C.border}` : "none" }}>
            <Typography fontSize={10} color={C.textMuted} mb={0.3} fontWeight={500} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{m.label}</Typography>
            <Typography fontSize={19} fontWeight={700} color={C.textPrimary} lineHeight={1.1} mb={0.3}>{m.value}</Typography>
            <Box sx={{ display:"inline-block", background:m.subBg, color:m.subC, fontSize:9, fontWeight:600, px:"5px", py:"2px", borderRadius:"3px", whiteSpace:"nowrap" }}>{m.sub}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// CARD WRAPPER
// ─────────────────────────────────────────────────────────────────
function Card({ title, icon, rightText, children, noPad, sx: sxExtra = {} }: CardProps) {
  return (
    <Box sx={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:"8px", p:noPad?0:"14px 16px", height:"100%", ...sxExtra }}>
      {title && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.2} sx={{ minHeight:"24px" }}>
          <Typography fontSize={12.5} fontWeight={600} color={C.textPrimary} display="flex" alignItems="center" gap={0.8}>
            {icon && <span style={{ fontSize:"14px" }}>{icon}</span>}
            <span>{title}</span>
          </Typography>
          {rightText && <Typography fontSize={11} color={C.textHint}>{rightText}</Typography>}
        </Stack>
      )}
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// DATA PROCESSING
// ─────────────────────────────────────────────────────────────────
function DataProcessing({ steps }: { steps: any[] }) {
  return (
    <Card title="Data Processing" icon="⚙">
      <Stack direction="row" alignItems="center">
        {steps.map((s: any, i: number) => (
          <Stack key={s.label} direction="row" alignItems="center" sx={{ flex:1 }}>
            <Box sx={{ flex:1, background:"#f8f9ff", border:`1px solid #e0e4f0`, borderRadius:"7px", p:"10px 8px", textAlign:"center" }}>
              <Typography fontSize={10.5} color={C.textMuted} mb={0.4}>{s.label}</Typography>
              <Typography fontSize={20} fontWeight={700} color={C.textPrimary} lineHeight={1.1}>{s.num}</Typography>
              <Box sx={{ display:"inline-block", background:s.subBg, color:s.subC, fontSize:10, fontWeight:700, px:"7px", py:"2px", borderRadius:"4px", mt:0.5 }}>{s.sub}</Box>
            </Box>
            {i < steps.length-1 && <Box sx={{ color:"#93c5fd", fontSize:18, px:"5px", flexShrink:0 }}>→</Box>}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// LEAD TIERS
// ─────────────────────────────────────────────────────────────────
function LeadTiers({ leadTiersData }: { leadTiersData: any }) {
  const { tiers = [], high_quality = "", low_quality = "" } = leadTiersData || {};
  return (
    <Card title="Lead Tiers - Quality & Priority" rightText="Based on fit, intent & engagement">
      <Grid container spacing={1}>
        {tiers.map((t: any) => (
          <Grid item xs={3} key={t.badge}>
            <Box sx={{ border:`1.5px solid ${t.borderColor}`, borderRadius:"7px", overflow:"hidden", height:"100%", display:"flex", flexDirection:"column" }}>
              <Box sx={{ background:t.headerBg, px:"10px", py:"8px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"6px" }}>
                <Typography fontSize={11} fontWeight={700} color="#ffffff" lineHeight={1.3} sx={{ flex:1 }}>{t.title}</Typography>
                <Box sx={{ background:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.35)", color:"#ffffff", borderRadius:"4px", fontSize:11, fontWeight:700, px:"6px", py:"1px", flexShrink:0 }}>{t.badge}</Box>
              </Box>
              <Box sx={{ background:t.bodyBg, px:"10px", pt:"10px", pb:"10px", flex:1, display:"flex", flexDirection:"column" }}>
                <Typography fontSize={27} fontWeight={700} color={t.countColor} lineHeight={1} mb={0.2}>{t.count}</Typography>
                <Typography fontSize={10.5} fontWeight={500} color={t.countColor} sx={{ opacity:0.75 }} mb={1.2}>{t.pct}</Typography>
                <Box sx={{ fontSize:10.5, lineHeight:1.9, color:t.checkColor, fontWeight:500, flex:1 }}>
                  {(t.checks || []).map((c: string, i: number) => <div key={i}>{c}</div>)}
                </Box>
              </Box>
              <Box sx={{ background:t.convertBg, color:t.convertText, py:"7px", textAlign:"center", fontSize:11.5, fontWeight:700, borderTop:`1px solid ${t.borderColor}` }}>{t.convert}</Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1} mt={0.8}>
        <Grid item xs={6}><Box sx={{ background:C.greenLight, color:C.greenText, borderRadius:"5px", p:"8px 12px", fontSize:11.5, fontWeight:600 }}>✓ {high_quality}</Box></Grid>
        <Grid item xs={6}><Box sx={{ background:C.redLight, color:C.red, border:`1px solid #fca5a5`, borderRadius:"5px", p:"8px 12px", fontSize:11.5, fontWeight:600 }}>⚠ {low_quality}</Box></Grid>
      </Grid>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// FUNNEL
// ─────────────────────────────────────────────────────────────────
function Funnel({ steps }: { steps: any[] }) {
  return (
    <Card title="Funnel" icon="▽">
      <Box sx={{ display:"flex", alignItems:"stretch", gap:"2px", mb:"6px" }}>
        {steps.map((s: any, i: number) => (
          <Box key={i} sx={{ flex:1, background:s.bg, borderRadius:i===0?"6px 0 0 6px":i===steps.length-1?"0 6px 6px 0":"0", px:"5px", py:"10px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minWidth:0 }}>
            <Typography fontSize={9} fontWeight={600} color="#fff" textAlign="center" lineHeight={1.25} sx={{ wordBreak:"break-word", mb:"3px" }}>{s.label}</Typography>
            <Typography fontSize={13} fontWeight={800} color="#fff" lineHeight={1}>{s.value}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display:"flex", gap:"2px" }}>
        {steps.map((s: any, i: number) => (
          <Box key={i} sx={{ flex:1, display:"flex", justifyContent:"center" }}>
            <Box sx={{ background:"#EEF2FF", color:"#3730a3", fontSize:9.5, fontWeight:700, px:"5px", py:"2px", borderRadius:"3px", whiteSpace:"nowrap" }}>{s.pct}</Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// REVENUE PANEL
// ─────────────────────────────────────────────────────────────────
function RevenuePanel({ revenue }: { revenue: any }) {
  const line1: number[] = revenue?.line1 || [];
  const line2: number[] = revenue?.line2 || [];
  const max = Math.max(...line1, ...line2, 1);
  const W = 240, H = 80;
  const toPoints = (data: number[]) => data.map((v, i) => `${(i/(data.length-1))*W},${H-(v/max)*H}`).join(" ");
  const pts1 = toPoints(line1), pts2 = toPoints(line2);
  const fillPts1 = `0,${H} ${pts1} ${W},${H}`;

  return (
    <Box sx={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:"8px", p:"14px 16px", height:"100%", display:"flex", flexDirection:"column" }}>
      <Typography fontSize={11} fontWeight={700} color={C.textMuted} mb={1} letterSpacing={0.3}>$ REVENUE</Typography>
      <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"6px", mb:1.5 }}>
        <Box><Typography fontSize={16} fontWeight={700} color={C.textPrimary} lineHeight={1.1}>{revenue?.total_signup ?? "—"}</Typography><Typography fontSize={9.5} color={C.textMuted} lineHeight={1.3}>Total Leads</Typography></Box>
        <Box><Typography fontSize={16} fontWeight={700} color={C.textPrimary} lineHeight={1.1}>{revenue?.paid_rate ?? "—"}</Typography><Typography fontSize={9.5} color={C.textMuted} lineHeight={1.3}>Qualified %</Typography></Box>
        <Box><Typography fontSize={16} fontWeight={700} color={C.green} lineHeight={1.1}>{revenue?.revenue_total ?? "—"}</Typography><Typography fontSize={9.5} color={C.textMuted} lineHeight={1.3}>Est. Revenue</Typography></Box>
      </Box>
      <Box sx={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow:"visible", display:"block" }} preserveAspectRatio="none">
          <defs>
            <linearGradient id="rev-fill-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#93C5FD" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#93C5FD" stopOpacity="0.0" />
            </linearGradient>
          </defs>
          <polygon points={fillPts1} fill="url(#rev-fill-grad)" />
          <polyline points={pts1} fill="none" stroke="#60A5FA" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          <polyline points={pts2} fill="none" stroke="#1e3a8a" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// ICP PERFORMANCE TABLE
// ─────────────────────────────────────────────────────────────────
function IcpPerformance({ rows }: { rows: any[] }) {
  const hSx = { fontSize:10.5, fontWeight:600, color:C.textHint, py:"7px", px:"10px", borderBottom:`1px solid ${C.border}`, background:"#fafafa" };
  const cSx = { fontSize:11.5, py:"7px", px:"10px", borderBottom:`1px solid ${C.borderLight}`, color:C.textPrimary };
  if (!rows.length) return (
    <Card title="ICP Performance" icon="🎯">
      <Typography fontSize={12} color={C.textMuted} textAlign="center" py={2}>No ICP data available yet. Upload a CSV to get started.</Typography>
    </Card>
  );
  return (
    <Card title="ICP Performance" icon="🎯">
      <Box sx={{ overflowX:"auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>{["ICP Name","Leads","Click Rate","Conv. Rate","Revenue","Status"].map((h) => <TableCell key={h} sx={hSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r: any, i: number) => (
              <TableRow key={i} sx={{ "&:hover":{ background:"#fafbff" } }}>
                <TableCell sx={{ ...cSx, fontWeight:600 }}>{r.name}</TableCell>
                <TableCell sx={{ ...cSx, color:C.textMuted }}>{r.leads}</TableCell>
                <TableCell sx={cSx}>{r.clickRate}</TableCell>
                <TableCell sx={cSx}>{r.convRate}</TableCell>
                <TableCell sx={{ ...cSx, fontWeight:600 }}>{r.revenue}</TableCell>
                <TableCell sx={cSx}><Box sx={{ display:"inline-block", background:r.statusBg, color:r.statusC, fontSize:10.5, fontWeight:700, px:"7px", py:"2px", borderRadius:"4px" }}>{r.status}</Box></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// ALERTS & OPPORTUNITIES
// ─────────────────────────────────────────────────────────────────
function AlertsPanel({ alerts }: { alerts: any[] }) {
  return (
    <Box sx={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:"8px", p:"14px 16px", height:"100%", overflow:"hidden" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.2}>
        <Typography fontSize={12.5} fontWeight={600} color={C.textPrimary}>🔔 Alerts and Opportunities</Typography>
        <Typography fontSize={11} color={C.blue} sx={{ cursor:"pointer" }}>View all</Typography>
      </Stack>
      <Stack spacing={1}>
        {alerts.map((a: any) => (
          <Stack key={a.num} direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ width:21, height:21, borderRadius:"50%", background:a.color, color:"#fff", fontSize:10.5, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, mt:"1px" }}>{a.num}</Box>
            <Box>
              <Typography fontSize={11} fontWeight={600} color={C.textPrimary} lineHeight={1.3}>{a.title}</Typography>
              <Typography fontSize={9.5} color={C.textMuted} lineHeight={1.4}>{a.sub}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// DISCOVERY SOURCES
// ─────────────────────────────────────────────────────────────────
function DiscoverySources({ discoveryData }: { discoveryData: any }) {
  const bars: any[]   = discoveryData?.bars  || [];
  const total: string = discoveryData?.total || "0";
  const MAX_H = 120;
  return (
    <Card title="Discovery Sources">
      <Stack direction="row" alignItems="flex-end" spacing={1.5} sx={{ height:`${MAX_H}px`, mb:1 }}>
        {bars.map((b: any) => (
          <Box key={b.label} sx={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center" }}>
            <Box sx={{ width:"100%", height:`${(b.heightPct / 100) * MAX_H}px`, background:b.color, borderRadius:"4px 4px 0 0" }} />
            <Typography fontSize={10} color={C.textMuted} mt={0.5}>{b.label}</Typography>
          </Box>
        ))}
        {!bars.length && <Typography fontSize={12} color={C.textMuted} sx={{ m:"auto" }}>No source data yet</Typography>}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap="8px" mb={1}>
        {bars.map((b: any) => (
          <Stack key={b.label} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width:8, height:8, borderRadius:"50%", background:b.color }} />
            <Typography fontSize={10} color={C.textMuted}>{b.label} ({b.value})</Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ textAlign:"center", background:C.blueLight, color:C.blue, fontSize:11.5, fontWeight:700, py:"6px", borderRadius:"4px" }}>Total Leads: {total}</Box>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// LEAD SCORING
// ─────────────────────────────────────────────────────────────────
function LeadScoring({ scoringData }: { scoringData: any }) {
  const segments: any[] = scoringData?.segments || [];
  const total: number   = scoringData?.total    || 0;
  const r = 40, cx = 52, cy = 52;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const arcs = segments.map((s: any) => {
    const dash = (s.pct / 100) * circumference;
    const arc  = { ...s, dash, offset };
    offset += dash;
    return arc;
  });
  return (
    <Card title="Lead Scoring (Intent & Engagement)" icon="⚡">
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flexShrink:0 }}>
          <svg width={104} height={104} viewBox="0 0 104 104">
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={15} />
            {arcs.map((a: any) => (
              <circle key={a.label} cx={cx} cy={cy} r={r} fill="none" stroke={a.color} strokeWidth={15}
                strokeDasharray={`${a.dash} ${circumference - a.dash}`}
                strokeDashoffset={-a.offset}
                transform={`rotate(-90 ${cx} ${cy})`} />
            ))}
            <text x={cx} y={cy-5} textAnchor="middle" fontSize={14} fontWeight="bold" fill={C.textPrimary}>{total}</text>
            <text x={cx} y={cx+9} textAnchor="middle" fontSize={9.5} fill={C.textMuted}>leads</text>
          </svg>
        </Box>
        <Box sx={{ flex:1 }}>
          {segments.map((s: any) => (
            <Stack key={s.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ py:"5px", borderBottom:`1px solid ${C.borderLight}`, "&:last-child":{ borderBottom:"none" } }}>
              <Stack direction="row" alignItems="center" spacing={0.7}>
                <Box sx={{ width:10, height:10, borderRadius:"2px", background:s.color, flexShrink:0 }} />
                <Typography fontSize={12} color={C.textPrimary}>{s.label}</Typography>
                <Typography fontSize={12} color={C.textMuted} fontWeight={600}>{s.value}</Typography>
              </Stack>
              <Box sx={{ background:s.intentBg, color:s.intentColor, fontSize:10, fontWeight:700, px:"7px", py:"2px", borderRadius:"4px", whiteSpace:"nowrap" }}>{s.intent}</Box>
            </Stack>
          ))}
        </Box>
      </Stack>
      <Box sx={{ textAlign:"center", background:C.purpleLight, color:C.purple, fontSize:11.5, fontWeight:700, py:"6px", borderRadius:"4px", mt:1.2 }}>Total Scored Leads: {total}</Box>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// OUTREACH OVERVIEW
// ─────────────────────────────────────────────────────────────────
function OutreachOverview({ channels }: { channels: any[] }) {
  return (
    <Box sx={{ border:"1.5px dashed #90caf9", borderRadius:"8px", p:"14px 16px", background:C.white, height:"100%", display:"flex", flexDirection:"column" }}>
      <Typography fontSize={12.5} fontWeight={600} color={C.textPrimary} mb={1.2}>Outreach Overview</Typography>
      <Grid container spacing={1.2} sx={{ flex:1 }}>
        {channels.map((ch: any) => (
          <Grid item xs={6} key={ch.title} sx={{ display:"flex" }}>
            <Box sx={{ border:`1.5px solid ${ch.borderColor}`, borderRadius:"8px", overflow:"hidden", width:"100%", display:"flex", flexDirection:"column" }}>
              <Box sx={{ background:ch.headerBg, px:"14px", py:"12px", display:"flex", alignItems:"center", gap:"8px" }}>
                <Box sx={{ width:30, height:30, borderRadius:"5px", background:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>{ch.icon}</Box>
                <Typography fontSize={13} fontWeight={700} color="#ffffff">{ch.title}</Typography>
              </Box>
              <Box sx={{ background:ch.bodyBg, px:"14px", pt:"12px", pb:"10px", flex:1 }}>
                <Stack direction="row" alignItems="baseline" spacing={1} mb={1.5}>
                  <Typography fontSize={28} fontWeight={700} color={ch.headerBg} lineHeight={1}>{ch.sent}</Typography>
                  <Typography fontSize={13} fontWeight={500} color={ch.headerBg} sx={{ opacity:0.75 }}>Total</Typography>
                </Stack>
                <Stack spacing={0}>
                  {(ch.rows || []).map((row: any) => (
                    <Box key={row.label} sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", py:"7px", borderTop:`1px solid rgba(0,0,0,0.08)` }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width:20, height:20, borderRadius:"50%", background:"rgba(255,255,255,0.65)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <span style={{ color:ch.headerBg, fontSize:11, fontWeight:700 }}>↑</span>
                        </Box>
                        <Typography fontSize={13} fontWeight={600} color={ch.headerBg}>{row.val}</Typography>
                        <Typography fontSize={12} color={ch.headerBg} sx={{ opacity:0.8 }}>{row.label}</Typography>
                      </Stack>
                      <Typography fontSize={12} fontWeight={600} color={ch.headerBg} sx={{ opacity:0.7 }}>{row.pct}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// DISCOVERY SOURCES BREAKDOWN TABLE
// ─────────────────────────────────────────────────────────────────
function SourcesBreakdown({ breakdownData }: { breakdownData: any }) {
  const rows: any[]        = breakdownData?.rows       || [];
  const totals: any[]      = breakdownData?.totals     || [];
  const grandTotal: string = breakdownData?.grand_total || "0";
  const hSx = { fontSize:10.5, fontWeight:600, color:C.textHint, py:"8px", px:"12px", borderBottom:`1px solid ${C.border}`, background:"#fafafa" };
  const cSx = { fontSize:11.5, py:"8px", px:"12px", borderBottom:`1px solid ${C.borderLight}`, color:C.textPrimary };
  return (
    <Card title="Discovery Sources Breakdown">
      <Box sx={{ overflowX:"auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>{["Source","Leads","Tier 1","Tier 2","Tier 3","Tier 4"].map((h) => <TableCell key={h} sx={hSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r: any, i: number) => (
              <TableRow key={i} sx={{ "&:hover":{ background:"#fafbff" } }}>
                <TableCell sx={cSx}>
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <Box sx={{ width:8, height:8, borderRadius:"50%", background:r.color ?? C.srcConsultants, flexShrink:0 }} />
                    <span>{r.src}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...cSx, color:C.textMuted }}>{r.leads}</TableCell>
                {[r.t1, r.t2, r.t3, r.t4].map((cell: any, ti: number) => (
                  <TableCell key={ti} sx={cSx}>
                    <Box sx={{ display:"inline-block", background:cell?.bg, color:cell?.c, fontSize:10.5, fontWeight:600, px:"7px", py:"2px", borderRadius:"4px" }}>{cell?.value}</Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow sx={{ background:"#f5f7ff" }}>
              <TableCell sx={{ ...cSx, fontWeight:700, fontSize:10.5 }}>TOTAL</TableCell>
              <TableCell sx={{ ...cSx, fontWeight:700 }}>{grandTotal}</TableCell>
              {totals.map((t: any, i: number) => (
                <TableCell key={i} sx={cSx}>
                  <Box sx={{ display:"inline-block", background:t.bg, color:t.c, fontSize:10.5, fontWeight:700, px:"8px", py:"2px", borderRadius:"4px" }}>{t.val}</Box>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────
// LEADS TABLE WITH PUSH TO INSTANTLY BUTTON
// ─────────────────────────────────────────────────────────────────
function SenderMetrics({ rows }: { rows: any[] }) {
  // rows come from /leads?status=sent — already-pushed leads for reference display
  // The real push action is in LeadsTable below
  const hSx = { fontSize:10.5, fontWeight:600, color:C.textHint, py:"7px", px:"9px", borderBottom:`1px solid ${C.border}`, background:"#fafafa" };
  const cSx = { fontSize:11, py:"7px", px:"9px", borderBottom:`1px solid ${C.borderLight}`, color:C.textPrimary };
  if (!rows.length) return (
    <Card title="Pushed Leads" rightText="enrichment_status=sent">
      <Typography fontSize={12} color={C.textMuted} textAlign="center" py={2}>No leads pushed yet. Upload a CSV or use the Leads table below to push.</Typography>
    </Card>
  );
  // return (
  //   <Card title="Pushed Leads" rightText="enrichment_status=sent">
  //     <Box sx={{ overflowX:"auto" }}>
  //       <Table size="small">
  //         <TableHead>
  //           <TableRow>{["Industry / Company","ICP","Tier","Sent","Open","Click","Bounce"].map((h) => <TableCell key={h} sx={hSx}>{h}</TableCell>)}</TableRow>
  //         </TableHead>
  //         <TableBody>
  //           {rows.map((r: any, i: number) => (
  //             <TableRow key={i} sx={{ "&:hover":{ background:"#fafbff" } }}>
  //               <TableCell sx={{ ...cSx, color:C.blue, fontWeight:500 }}>{r.source}</TableCell>
  //               <TableCell sx={cSx}>{r.icp}</TableCell>
  //               <TableCell sx={cSx}><Box sx={{ display:"inline-block", background:C.greenLight, color:C.green, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px" }}>{r.tier ?? "T1"}</Box></TableCell>
  //               <TableCell sx={cSx}>{r.sent}</TableCell>
  //               <TableCell sx={cSx}>{r.open}</TableCell>
  //               <TableCell sx={cSx}>{r.click}</TableCell>
  //               <TableCell sx={cSx}>
  //                 <Stack direction="row" alignItems="center" spacing={0.5}>
  //                   <span>{r.bounce}</span>
  //                   <Box sx={{ display:"inline-block", background:r.bounceLevel==="High"?C.redLight:C.greenLight, color:r.bounceLevel==="High"?C.red:C.green, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px" }}>{r.bounceLevel}</Box>
  //                 </Stack>
  //               </TableCell>
  //             </TableRow>
  //           ))}
  //         </TableBody>
  //       </Table>
  //     </Box>
  //   </Card>
  // );
}

// ─────────────────────────────────────────────────────────────────
// PUSH TO INSTANTLY MODAL
// Server-side tier filter + search (name/email) + pagination.
//
// - Search is debounced ~300ms and sent to the API as `q`.
// - Pagination is driven by the server's total_pages / has_next / has_prev.
// - Push status is keyed by a STABLE lead identity (email||linkedin_url||name)
//   instead of array index, because the row set changes between pages.
// API: GET /leads?tier=...&q=...&page=...&per_page=...  →  POST /push-lead per lead
// ─────────────────────────────────────────────────────────────────
function PushToInstantlyModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const [leads,       setLeads]       = useState<any[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError,   setListError]   = useState<string | null>(null);

  // push status keyed by stable lead key (NOT array index — page changes reorder rows)
  const [pushStatus,  setPushStatus]  = useState<Record<string, "idle"|"pushing"|"done"|"error">>({});
  const [pushMsg,     setPushMsg]     = useState<Record<string, string>>({});
  const [bulkPushing, setBulkPushing] = useState(false);
  const [bulkResult,  setBulkResult]  = useState<string | null>(null);

  const [filterTier,  setFilterTier]  = useState("Tier 1");

  // search (raw input) + debounced value actually sent to the API
  const [search,          setSearch]          = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // server-driven pagination
  const [page,        setPage]        = useState(1);
  const [perPage,     setPerPage]     = useState(25);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [hasNext,     setHasNext]     = useState(false);
  const [hasPrev,     setHasPrev]     = useState(false);

  // stable identity for a lead, used as the key for push status
  const leadKey = (l: any) =>
    String(l?.email || l?.linkedin_url || l?.full_name || JSON.stringify(l));

  const hasValidEmail = (l: any) =>
    l?.email && l.email !== "nan" && l.email !== "None" && String(l.email).includes("@");

  const icp = (v: string) =>
    v === "LinkedinCreator" ? "LinkedIn Creator" : v === "CourseCreator" ? "Course Creator" : v || "—";

  // ── Debounce the search box (300ms) ───────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Whenever tier, debounced search, or perPage changes → reset to page 1
  useEffect(() => {
    setPage(1);
  }, [filterTier, debouncedSearch, perPage]);

  // ── Fetch from server whenever any query input changes ─────────────
  const loadLeads = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const data = await fetchAllLeads({
        tier:     filterTier,        // "" for the "All" tab
        q:        debouncedSearch,
        page,
        per_page: perPage,
      });
      setLeads(data.leads ?? []);
      setTotalPages(data.total_pages ?? 1);
      setTotalCount(data.total ?? 0);
      setHasNext(!!data.has_next);
      setHasPrev(!!data.has_prev);
    } catch (e: any) {
      setListError(e.message);
      setLeads([]);
      setTotalPages(1);
      setTotalCount(0);
      setHasNext(false);
      setHasPrev(false);
    } finally {
      setLoadingList(false);
    }
  }, [filterTier, debouncedSearch, page, perPage]);

  // Refetch on open and whenever query inputs change
  useEffect(() => {
    if (open) loadLeads();
  }, [open, loadLeads]);

  // Reset transient UI state when the modal closes
  useEffect(() => {
    if (!open) {
      setSearch(""); setDebouncedSearch("");
      setPage(1); setBulkResult(null);
      setPushStatus({}); setPushMsg({});
    }
  }, [open]);

  const withEmailOnPage = leads.filter(hasValidEmail).length;

  // Push a single lead → POST /push-lead
  const pushOne = async (lead: any) => {
    const key = leadKey(lead);
    setPushStatus((s) => ({ ...s, [key]: "pushing" }));
    try {
      const result = await pushLeadToInstantly(lead);
      if (result?.success || result?.status_code === 200 || result?.status_code === 201 || result?.status_code === 409) {
        setPushStatus((s) => ({ ...s, [key]: "done" }));
        setPushMsg((m) => ({ ...m, [key]: result?.status_code === 409 ? "Already in campaign" : "Pushed ✓" }));
      } else {
        throw new Error(result?.response?.message ?? result?.response ?? "Unknown error");
      }
    } catch (e: any) {
      setPushStatus((s) => ({ ...s, [key]: "error" }));
      setPushMsg((m) => ({ ...m, [key]: e.message || "Error" }));
    }
  };

  // Push every lead WITH EMAIL on the CURRENT PAGE, sequentially.
  // (Pushing the entire result set across all pages would require fetching all
  //  pages first; current-page bulk push is the predictable, safe behavior.)
  const pushAllOnPage = async () => {
    const eligible = leads.filter(hasValidEmail);
    if (!eligible.length) { setBulkResult("No leads with valid email on this page."); return; }
    setBulkPushing(true); setBulkResult(null);
    let done = 0; let errs = 0;
    for (const lead of eligible) {
      const key = leadKey(lead);
      setPushStatus((s) => ({ ...s, [key]: "pushing" }));
      try {
        const result = await pushLeadToInstantly(lead);
        const ok = result?.success || [200,201,409].includes(result?.status_code);
        setPushStatus((s) => ({ ...s, [key]: ok ? "done" : "error" }));
        setPushMsg((m) => ({ ...m, [key]: ok ? (result?.status_code === 409 ? "Already in campaign" : "Pushed ✓") : (result?.response ?? "Failed") }));
        if (ok) done++; else errs++;
      } catch (e: any) {
        setPushStatus((s) => ({ ...s, [key]: "error" }));
        setPushMsg((m) => ({ ...m, [key]: e.message }));
        errs++;
      }
      await new Promise((r) => setTimeout(r, 120));
    }
    setBulkPushing(false);
    setBulkResult(`Done: ${done} pushed, ${errs} errors.`);
    if (done > 0) setTimeout(onDone, 1500);
  };

  const tiers = ["Tier 1", "Tier 2", "Tier 3", "All"];
  const startIdx = (page - 1) * perPage;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:780, maxWidth:"96vw", bgcolor:C.white, borderRadius:"12px", boxShadow:"0 24px 48px rgba(0,0,0,0.22)", outline:"none", display:"flex", flexDirection:"column", maxHeight:"90vh" }}>
        {/* Header */}
        <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", p:"14px 20px", borderBottom:`1px solid ${C.borderLight}`, flexShrink:0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography fontSize={17} fontWeight={700} color={C.textPrimary}>🚀 Push to Instantly</Typography>
            <Typography fontSize={11} color={C.textMuted}>POST /push-lead</Typography>
          </Stack>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>

        {/* Tier filter + bulk push */}
        <Box sx={{ px:"20px", py:"10px", borderBottom:`1px solid ${C.borderLight}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, flexWrap:"wrap", gap:1 }}>
          <Stack direction="row" spacing={0.8} flexWrap="wrap" alignItems="center">
            {tiers.map((t) => {
              const tierVal = t === "All" ? "" : t;
              const active  = filterTier === tierVal;
              return (
                <Box key={t} onClick={() => setFilterTier(tierVal)}
                  sx={{ px:"10px", py:"5px", borderRadius:"5px", fontSize:11.5, fontWeight:600, cursor:"pointer",
                    background: active ? C.blue : "#f3f4f6",
                    color:      active ? "#fff"  : C.textMuted,
                    border:     active ? `1px solid ${C.blue}` : "1px solid transparent",
                  }}>
                  {t}
                </Box>
              );
            })}
            <Typography fontSize={11} color={C.textMuted} sx={{ alignSelf:"center", ml:1 }}>
              {totalCount} match{totalCount === 1 ? "" : "es"} • {withEmailOnPage} with email (this page)
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {bulkResult && <Typography fontSize={11} color={C.green} fontWeight={600}>{bulkResult}</Typography>}
            <Button size="small" disabled={bulkPushing || !withEmailOnPage} onClick={pushAllOnPage}
              sx={{ textTransform:"none", fontSize:12, fontWeight:700, color:"#fff", background:C.blue, borderRadius:"6px", px:"16px", py:"6px",
                "&:hover":{ background:"#1a3565" }, "&:disabled":{ opacity:0.5, color:"#fff" } }}>
              {bulkPushing ? "Pushing…" : `⚡ Push Page (${withEmailOnPage})`}
            </Button>
          </Stack>
        </Box>

        {/* Search bar */}
        <Box sx={{ px:"20px", py:"10px", borderBottom:`1px solid ${C.borderLight}`, display:"flex", alignItems:"center", gap:1.5, flexShrink:0, flexWrap:"wrap" }}>
          <Box sx={{ position:"relative", flex:1, minWidth:220 }}>
            <Box sx={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:13, color:C.textHint, pointerEvents:"none" }}>🔍</Box>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={{
                width:"100%", boxSizing:"border-box", padding:"8px 30px 8px 30px",
                fontSize:13, borderRadius:6, border:`1px solid ${C.border}`,
                color:C.textPrimary, outline:"none", background:"#fff",
              }}
            />
            {search && (
              <Box onClick={() => setSearch("")}
                sx={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", fontSize:13, color:C.textHint, cursor:"pointer", lineHeight:1 }}>
                ✕
              </Box>
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Typography fontSize={11} color={C.textMuted}>Per page</Typography>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              style={{ fontSize:12, padding:"5px 8px", borderRadius:5, border:`1px solid ${C.border}`, color:C.textPrimary, background:"#fff", outline:"none" }}>
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Stack>
        </Box>

        {/* Leads table */}
        <Box sx={{ flex:1, overflowY:"auto", px:"20px", py:"10px" }}>
          {listError && <Alert severity="error" sx={{ mb:1 }}>{listError}</Alert>}
          {loadingList && <Typography fontSize={12} color={C.textMuted} textAlign="center" py={3}>Loading leads…</Typography>}
          {!loadingList && !leads.length && !listError && (
            <Typography fontSize={12} color={C.textMuted} textAlign="center" py={3}>
              {debouncedSearch ? `No leads match “${debouncedSearch}”.` : "No leads found for this tier."}
            </Typography>
          )}
          {!loadingList && leads.length > 0 && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Name","Company","ICP","Tier","Email","Status","Action"].map((h) => (
                    <TableCell key={h} sx={{ fontSize:10.5, fontWeight:700, color:C.textHint, py:"6px", px:"8px", background:"#fafafa", borderBottom:`1px solid ${C.border}` }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead: any) => {
                  const key      = leadKey(lead);
                  const status   = pushStatus[key] ?? "idle";
                  const msg      = pushMsg[key];
                  const hasEmail = hasValidEmail(lead);
                  const pushed   = !!lead.instantly_pushed_at;
                  const cSx2     = { fontSize:11, py:"7px", px:"8px", borderBottom:`1px solid ${C.borderLight}`, color:C.textPrimary };
                  return (
                    <TableRow key={key} sx={{ "&:hover":{ background:"#f8faff" }, opacity: status === "done" ? 0.75 : 1 }}>
                      <TableCell sx={{ ...cSx2, fontWeight:600, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.full_name || "—"}</TableCell>
                      <TableCell sx={{ ...cSx2, color:C.textMuted, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.company || "—"}</TableCell>
                      <TableCell sx={cSx2}><Box sx={{ display:"inline-block", background:C.blueLight, color:C.blue, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px" }}>{icp(lead.primary_icp)}</Box></TableCell>
                      <TableCell sx={cSx2}><Box sx={{ display:"inline-block", fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px",
                        background: lead.priority_tier === "Tier 1" ? "#dcfce7" : lead.priority_tier === "Tier 2" ? "#dbeafe" : "#fef3c7",
                        color:      lead.priority_tier === "Tier 1" ? "#166534" : lead.priority_tier === "Tier 2" ? "#1e40af" : "#92400e",
                      }}>{lead.priority_tier || "—"}</Box></TableCell>
                      <TableCell sx={{ ...cSx2, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {hasEmail ? <span style={{ color:C.green }}>{lead.email}</span> : <span style={{ color:C.textHint }}>No email</span>}
                      </TableCell>
                      <TableCell sx={cSx2}>
                        {status === "idle" && pushed && <Box sx={{ display:"inline-block", background:C.greenLight, color:C.green, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px" }}>Already pushed</Box>}
                        {status === "idle" && !pushed && hasEmail && <Box sx={{ display:"inline-block", background:"#f3f4f6", color:C.textMuted, fontSize:9.5, fontWeight:600, px:"5px", py:"1px", borderRadius:"3px" }}>Ready</Box>}
                        {status === "idle" && !pushed && !hasEmail && <Box sx={{ display:"inline-block", background:C.amberLight, color:C.amber, fontSize:9.5, fontWeight:600, px:"5px", py:"1px", borderRadius:"3px" }}>No email</Box>}
                        {status === "pushing" && <Box sx={{ display:"inline-block", background:C.blueLight, color:C.blue, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px" }}>Pushing…</Box>}
                        {status === "done" && <Box sx={{ display:"inline-block", background:C.greenLight, color:C.green, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px" }}>{msg}</Box>}
                        {status === "error" && <Box sx={{ display:"inline-block", background:C.redLight, color:C.red, fontSize:9.5, fontWeight:700, px:"5px", py:"1px", borderRadius:"3px", maxWidth:130, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={msg}>{msg}</Box>}
                      </TableCell>
                      <TableCell sx={cSx2}>
                        <Button size="small" disabled={!hasEmail || status === "pushing" || status === "done"} onClick={() => pushOne(lead)}
                          sx={{ textTransform:"none", fontSize:10.5, fontWeight:700, color:"#fff", background: status === "done" ? C.green : C.blue, borderRadius:"5px", px:"10px", py:"3px", minWidth:70,
                            "&:hover":{ background:"#1a3565" }, "&:disabled":{ opacity: status === "done" ? 0.6 : 0.4, color:"#fff" } }}>
                          {status === "pushing" ? "…" : status === "done" ? "✓ Done" : "Push →"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>

        {/* Footer: server-driven pagination + close */}
        <Box sx={{ p:"10px 20px", borderTop:`1px solid ${C.borderLight}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0, flexWrap:"wrap", gap:1 }}>
          {totalCount > 0 ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button size="small" disabled={!hasPrev || loadingList} onClick={() => setPage((p) => Math.max(1, p - 1))}
                sx={{ textTransform:"none", fontSize:12, color:C.textPrimary, border:`1px solid ${C.border}`, borderRadius:"6px", px:"12px", py:"4px", minWidth:0, "&:disabled":{ opacity:0.4 } }}>
                ‹ Prev
              </Button>
              <Typography fontSize={12} color={C.textMuted}>
                Page {page} of {totalPages}
                <span style={{ marginLeft: 8, color: C.textHint }}>
                  ({totalCount === 0 ? 0 : startIdx + 1}–{Math.min(startIdx + perPage, totalCount)} of {totalCount})
                </span>
              </Typography>
              <Button size="small" disabled={!hasNext || loadingList} onClick={() => setPage((p) => p + 1)}
                sx={{ textTransform:"none", fontSize:12, color:C.textPrimary, border:`1px solid ${C.border}`, borderRadius:"6px", px:"12px", py:"4px", minWidth:0, "&:disabled":{ opacity:0.4 } }}>
                Next ›
              </Button>
            </Stack>
          ) : <Box />}

          <Button onClick={onClose} sx={{ textTransform:"none", fontSize:13, color:C.textMuted, border:`1px solid ${C.border}`, borderRadius:"6px", px:"16px", py:"6px" }}>Close</Button>
        </Box>
      </Box>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// SCRAPER CONTROL MODAL — start/stop scraper, show status
// APIs: POST /scheduler/start  POST /scheduler/stop
// ─────────────────────────────────────────────────────────────────
function ScraperModal({ open, onClose, schedulerData, onRefresh }: { open: boolean; onClose: () => void; schedulerData: any; onRefresh: () => void }) {
  const [acting,      setActing]      = useState(false);
  const [actionMsg,   setActionMsg]   = useState<string | null>(null);
  const [actionErr,   setActionErr]   = useState<string | null>(null);
  const [intervalH,   setIntervalH]   = useState(6);

  const scraper    = schedulerData?.scraper_scheduler ?? {};
  const isRunning  = scraper.running ?? false;
  const autopush   = schedulerData?.auto_push_scheduler ?? {};

  const doStart = async () => {
    setActing(true); setActionMsg(null); setActionErr(null);
    try {
      await startScraper(intervalH);
      setActionMsg(`Scraper started — runs every ${intervalH}h`);
      setTimeout(onRefresh, 1000);
    } catch (e: any) { setActionErr(e.message); }
    finally { setActing(false); }
  };

  const doStop = async () => {
    setActing(true); setActionMsg(null); setActionErr(null);
    try {
      await stopScraper();
      setActionMsg("Scraper stopped.");
      setTimeout(onRefresh, 1000);
    } catch (e: any) { setActionErr(e.message); }
    finally { setActing(false); }
  };

  const statRow = (label: string, val: any) => (
    <Stack key={label} direction="row" justifyContent="space-between" sx={{ py:"5px", borderBottom:`1px solid ${C.borderLight}` }}>
      <Typography fontSize={12} color={C.textMuted}>{label}</Typography>
      <Typography fontSize={12} fontWeight={600} color={C.textPrimary}>{val ?? "—"}</Typography>
    </Stack>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:480, maxWidth:"96vw", bgcolor:C.white, borderRadius:"12px", boxShadow:"0 24px 48px rgba(0,0,0,0.2)", outline:"none" }}>
        <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", p:"14px 20px", borderBottom:`1px solid ${C.borderLight}` }}>
          <Typography fontSize={17} fontWeight={700} color={C.textPrimary}>⚙ Scraper Scheduler</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p:"20px" }}>
          {/* Status */}
          <Box sx={{ p:"12px 14px", borderRadius:"8px", background: isRunning ? C.greenLight : C.amberLight, border:`1px solid ${isRunning ? "#86efac" : "#fde68a"}`, mb:2 }}>
            <Typography fontSize={13} fontWeight={700} color={isRunning ? C.green : C.amber}>
              {isRunning ? "🟢 Scraper is RUNNING" : "🟡 Scraper is STOPPED"}
            </Typography>
            {isRunning && scraper.next_run && (
              <Typography fontSize={11} color={C.textMuted} mt={0.3}>Next run: {new Date(scraper.next_run).toLocaleString()}</Typography>
            )}
          </Box>

          {/* Scraper stats */}
          <Box sx={{ mb:2 }}>
            {[
              ["Total cycles", scraper.total_cycles],
              ["Total leads found", scraper.total_leads],
              ["Last run", scraper.last_run ? new Date(scraper.last_run).toLocaleString() : "Never"],
              ["Auto-push sent today", `${autopush.sent_today ?? 0} / ${(autopush.sent_today ?? 0) + (autopush.remaining_today ?? 0)}`],
              ["Daily limit remaining", autopush.remaining_today ?? 0],
            ].map(([k, v]) => statRow(String(k), v))}
          </Box>

          {actionMsg && <Alert severity="success" sx={{ mb:1.5 }}>{actionMsg}</Alert>}
          {actionErr && <Alert severity="error"   sx={{ mb:1.5 }}>{actionErr}</Alert>}

          {/* Interval picker + start/stop */}
          {!isRunning && (
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <Typography fontSize={12} color={C.textMuted} sx={{ whiteSpace:"nowrap" }}>Interval (hours):</Typography>
              {[1,3,6,12,24].map((h) => (
                <Box key={h} onClick={() => setIntervalH(h)} sx={{ px:"10px", py:"5px", borderRadius:"5px", fontSize:12, cursor:"pointer", fontWeight:600,
                  background: intervalH===h ? C.blue : "#f3f4f6", color: intervalH===h ? "#fff" : C.textMuted, border: intervalH===h ? `1px solid ${C.blue}` : "1px solid transparent" }}>
                  {h}h
                </Box>
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1}>
            {!isRunning ? (
              <Button fullWidth onClick={doStart} disabled={acting}
                sx={{ textTransform:"none", fontSize:13, fontWeight:700, color:"#fff", background:C.green, borderRadius:"6px", py:"8px", "&:hover":{ background:"#1b5e20" }, "&:disabled":{ opacity:0.5, color:"#fff" } }}>
                {acting ? "Starting…" : "▶ Start Scraper"}
              </Button>
            ) : (
              <Button fullWidth onClick={doStop} disabled={acting}
                sx={{ textTransform:"none", fontSize:13, fontWeight:700, color:"#fff", background:C.red, borderRadius:"6px", py:"8px", "&:hover":{ background:"#b71c1c" }, "&:disabled":{ opacity:0.5, color:"#fff" } }}>
                {acting ? "Stopping…" : "⏹ Stop Scraper"}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────
// FIX: added onTriggerNow prop → wired to POST /scheduler/trigger-now
function Footer({ schedulerData, onTriggerNow }: { schedulerData: any; onTriggerNow?: () => void }) {
  const nextUpdate     = schedulerData?.auto_push_scheduler?.next_check ?? null;
  const schedulerOn    = schedulerData?.auto_push_scheduler?.running ?? false;
  const sentToday      = schedulerData?.auto_push_scheduler?.sent_today      ?? 0;
  const remainingToday = schedulerData?.auto_push_scheduler?.remaining_today ?? 0;
  const [triggering, setTriggering] = useState(false);

  const doTrigger = async () => {
    if (!onTriggerNow) return;
    setTriggering(true);
    try { await onTriggerNow(); } finally { setTimeout(() => setTriggering(false), 3000); }
  };

  return (
    <Box sx={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:"8px", p:"9px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", mt:1.5, flexWrap:"wrap", gap:1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontSize={12} color={C.blue}>ℹ</Typography>
        <Typography fontSize={10.5} color={C.textHint}>
          MongoDB + SQLite live •
          APScheduler: <strong style={{ color: schedulerOn ? C.green : C.amber }}>{schedulerOn ? "Active ✓" : "Stopped"}</strong>
          {" • "}Sent today: {sentToday} / {sentToday + remainingToday}
          {nextUpdate ? ` • Next check: ${new Date(nextUpdate).toLocaleTimeString()}` : ""}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {[{ label:"Tier 1", color:"#065D31" },{ label:"Tier 2", color:"#001F4F" },{ label:"Tier 3", color:"#774403" },{ label:"Tier 4", color:"#222A35" }].map((t) => (
          <Stack key={t.label} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width:8, height:8, borderRadius:"50%", background:t.color }} />
            <Typography fontSize={10.5} color={C.textMuted}>{t.label}</Typography>
          </Stack>
        ))}
        {/* FIX: Trigger Now button — calls POST /scheduler/trigger-now */}
        <Button size="small" onClick={doTrigger} disabled={triggering}
          sx={{ textTransform:"none", fontSize:11, fontWeight:600, color:C.blue, border:`1px solid ${C.blue}`, borderRadius:"5px", px:"10px", py:"3px", ml:1, "&:disabled":{ opacity:0.5 } }}>
          {triggering ? "Triggering…" : "▶ Trigger Now"}
        </Button>
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// LOADING BAR
// ─────────────────────────────────────────────────────────────────
function LoadingBar() {
  return (
    <Box sx={{ px:"18px", py:"10px", background:C.white, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:2 }}>
      <Box sx={{ width:16, height:16, borderRadius:"50%", border:`2px solid ${C.blue}`, borderTopColor:"transparent", animation:"spin 0.8s linear infinite", "@keyframes spin":{ to:{ transform:"rotate(360deg)" } } }} />
      <Typography fontSize={12} color={C.textMuted}>Loading dashboard data from API…</Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function CustomerAcquisitionDashboard() {
  // Raw API state
  const [dashData,      setDashData]      = useState<any>(null);
  const [statsData,     setStatsData]     = useState<any>(null);
  const [scrapedStats,  setScrapedStats]  = useState<any>(null);
  const [leadsData,     setLeadsData]     = useState<any>(null);
  const [schedulerData, setSchedulerData] = useState<any>(null);

  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState<string | null>(null);
  const [uploadModalOpen,  setUploadModalOpen]  = useState(false);
  const [pushModalOpen,    setPushModalOpen]    = useState(false);
  const [scraperModalOpen, setScraperModalOpen] = useState(false);

  // Fetch all data sources in parallel
  const loadData = useCallback(async (range?: DateRange | null) => {
    setLoading(true); setError(null);
    try {
      const [dash, stats, scraped, leads, scheduler] = await Promise.allSettled([
        fetchDashboardData(range),   // → { kpi_metrics, meta: { total_leads, with_email, pushed } }
        fetchLeadStats(),             // → { total, by_tier, by_icp, by_status }
        fetchScrapedStats(),          // → { total, ... }
        fetchLeads({ per_page: 50, status: "sent" }),    // → { total, leads: [...] } — enrichment_status="sent" = pushed to Instantly; used for Sender Metrics table
        fetchSchedulerStatus(),       // → { auto_push_scheduler, scraper_scheduler }
      ]);

      if (dash.status      === "fulfilled") setDashData(dash.value);
      else setError(`Dashboard: ${(dash as PromiseRejectedResult).reason?.message}`);

      if (stats.status     === "fulfilled") setStatsData(stats.value);
      if (scraped.status   === "fulfilled") setScrapedStats(scraped.value);
      if (leads.status     === "fulfilled") setLeadsData(leads.value);
      if (scheduler.status === "fulfilled") setSchedulerData(scheduler.value);
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDateChange     = (range: DateRange) => loadData(range);
  const handleUploadComplete = () => { loadData(); }; // FIX: immediate reload so stats update right after upload

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => loadData(), 60_000);
    return () => clearInterval(interval);
  }, [loadData]);

  // FIX: Trigger Now button handler (POST /scheduler/trigger-now then reload stats)
  const handleTriggerNow = async () => {
    try {
      await fetch(`${API_BASE}/scheduler/trigger-now`, { method: "POST" });
      setTimeout(() => loadData(), 2000); // reload after 2s to pick up pushed leads
    } catch (_) { /* ignore */ }
  };

  // Export Report — downloads a JSON summary of all current dashboard data
  const handleExportReport = () => {
    const report = {
      generated_at: new Date().toISOString(),
      meta:         dashData?.meta        ?? {},
      kpi_metrics:  dashData?.kpi_metrics ?? [],
      lead_stats:   statsData             ?? {},
      scheduler:    schedulerData         ?? {},
      scraped:      scrapedStats          ?? {},
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `dashboard-report-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Build all UI data from API responses
  const totalLeads       = dashData?.meta?.total_leads ?? 0;
  const kpiMetrics       = dashData?.kpi_metrics       ?? [];

  const dataProcessing   = (dashData && statsData)  ? buildDataProcessing(dashData, statsData)     : [];
  const leadTiersData    = (dashData && statsData)  ? buildLeadTiers(statsData, totalLeads)        : null;
  const funnelSteps      = (dashData && statsData)  ? buildFunnel(dashData, statsData)             : [];
  const revenueData      = (dashData && statsData)  ? buildRevenue(dashData, statsData)            : null;
  const icpPerf          = statsData                ? buildIcpPerformance(statsData, dashData)     : [];
  const alerts           = (dashData && statsData)  ? buildAlerts(schedulerData, statsData, dashData) : [];
  const discoveryData    = statsData                ? buildDiscoverySources(statsData, scrapedStats): null;
  const scoringData      = statsData                ? buildLeadScoring(statsData)                  : null;
  const outreachChannels = (dashData && statsData)  ? buildOutreachOverview(dashData, statsData)   : [];
  const breakdownData    = statsData                ? buildSourcesBreakdown(statsData)             : null;
  const senderRows       = leadsData                ? buildSenderMetrics(leadsData)                : [];

  const hasData = !!(dashData || statsData);

  return (
    <Box sx={{ minHeight:"100vh", background:C.pageBg }}>
      <CSVUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadComplete={handleUploadComplete}
      />

      {/* Push to Instantly modal */}
      <PushToInstantlyModal
        open={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
        onDone={() => { setPushModalOpen(false); loadData(); }}
      />

      {/* Scraper control modal */}
      <ScraperModal
        open={scraperModalOpen}
        onClose={() => setScraperModalOpen(false)}
        schedulerData={schedulerData}
        onRefresh={loadData}
      />

      <Header
        onDateChange={handleDateChange}
        onUploadClick={() => setUploadModalOpen(true)}
        onExportClick={handleExportReport}
        onPushClick={() => setPushModalOpen(true)}
        onScraperClick={() => setScraperModalOpen(true)}
      />

      {loading && <LoadingBar />}

      {error && (
        <Box sx={{ mx:"18px", mt:"10px", px:"14px", py:"10px", background:C.redLight, color:C.red, borderRadius:"6px", fontSize:12, border:`1px solid #fca5a5` }}>
          ⚠ Could not reach API ({API_BASE}): {error}. Make sure your FastAPI server is running and CORS is enabled.
        </Box>
      )}

      {kpiMetrics.length > 0 && <KpiMetricsBar metrics={kpiMetrics} />}

      {hasData && (
        <Box sx={{ p:"14px 18px" }}>

          {/* ROW 1 — Inbound | Metrics */}
          <Grid container spacing={1.5} mb={1.5}>
            <Grid item xs={12} md={6}>
              <Box sx={{ border:`1px solid ${C.border}`, borderRadius:"8px", background:C.white, p:"12px 14px", height:"100%" }}>
                <Typography fontSize={11} fontWeight={600} color={C.textMuted} letterSpacing={0.5} mb={1.2} sx={{ textTransform:"uppercase" }}>Inbound Metrics</Typography>
                <Stack spacing={1.5}>
                  {dataProcessing.length > 0 && <DataProcessing steps={dataProcessing} />}
                  {leadTiersData           && <LeadTiers leadTiersData={leadTiersData} />}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ border:`1px solid ${C.border}`, borderRadius:"8px", background:C.white, p:"12px 14px", height:"100%" }}>
                <Typography fontSize={11} fontWeight={600} color={C.textMuted} letterSpacing={0.5} mb={1.2} sx={{ textTransform:"uppercase" }}>Metrics</Typography>
                <Stack spacing={1.5}>
                  <Grid container spacing={1.2}>
                    <Grid item xs={7}>{funnelSteps.length > 0 && <Funnel steps={funnelSteps} />}</Grid>
                    {/* <Grid item xs={5}>{revenueData            && <RevenuePanel revenue={revenueData} />}</Grid> */}
                  </Grid>
                  <IcpPerformance rows={icpPerf} />
                  {alerts.length > 0 && <AlertsPanel alerts={alerts} />}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {/* ROW 2 — Discovery Sources + Lead Scoring + Outreach */}
          <Grid container spacing={1.5} mb={1.5} alignItems="stretch">
            <Grid item xs={12} md={4}>{discoveryData    && <DiscoverySources discoveryData={discoveryData} />}</Grid>
            <Grid item xs={12} md={3}>{scoringData      && <LeadScoring scoringData={scoringData} />}</Grid>
            <Grid item xs={12} md={5}>{outreachChannels.length > 0 && <OutreachOverview channels={outreachChannels} />}</Grid>
          </Grid>

          {/* ROW 3 — Sources Breakdown + Sender Metrics */}
          <Grid container spacing={1.5} mb={1.5}>
            <Grid item xs={12} md={6}>{breakdownData && <SourcesBreakdown breakdownData={breakdownData} />}</Grid>
            {/* <Grid item xs={12} md={6}><SenderMetrics rows={senderRows} /></Grid> */}
          </Grid>

          <Footer schedulerData={schedulerData} onTriggerNow={handleTriggerNow} />
        </Box>
      )}

      {!hasData && !loading && !error && (
        <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", py:8, gap:2 }}>
          <Typography fontSize={48}>📊</Typography>
          <Typography fontSize={18} fontWeight={600} color={C.textPrimary}>No data yet</Typography>
          <Typography fontSize={14} color={C.textMuted}>Upload a CSV or start the scraper to begin collecting leads.</Typography>
          <Button onClick={() => setUploadModalOpen(true)} sx={{ textTransform:"none", fontSize:13, fontWeight:500, color:"#fff", background:C.blue, borderRadius:"6px", px:"20px", py:"8px", "&:hover":{ background:"#1a3565" } }}>Upload CSV</Button>
        </Box>
      )}
    </Box>
  );
}
